import React, { useState, useRef, useCallback, useMemo, useEffect } from 'react';
import { useSelector } from 'react-redux';
import type { RootState } from '../store/store';
import { usePermissions } from '../hooks/usePermissions';
import { API_CONFIG } from '../config/api.config';
import { prepareCommonHeaders } from '../services/baseApi';
import { downloadCsv } from '../utils/csvExport';
import {
  groupAlarmLogEvents,
  filterGroupedAlarms,
  estimateBucketCountMs,
} from '../utils/analyticsReports';
import { filterAlarmLogsBySensorAccess } from '../utils/alarmAccess';
import {
  FileText,
  Download,
  Calendar,
  Thermometer,
  Wifi,
  WifiOff,
  AlertTriangle,
  Bell,
  CheckCircle,
  Clock,
  BarChart3,
  Loader2,
  ChevronDown,
  ChevronUp,
  Table2,
  Mail,
} from 'lucide-react';
import { Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler,
} from 'chart.js';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import { format, subHours } from 'date-fns';

import { useGetSensorListQuery } from '../services/sensorApi';
import { useGetLiveDataQuery, useGetSensorReadingsQuery } from '../services/sensorDataApi';
import { useGetZonesQuery, useGetZoneSensorsQuery } from '../services/zoneApi';
import { useGetUsersQuery } from '../services/userApi';
import { useGetAlarmLogQuery, useGetAlarmsQuery, useGetRemoteAlertGroupsQuery } from '../services/alarmApi';
import { useGetBaseUnitListQuery, useGetBaseUnitTypesQuery } from '../services/baseUnitApi';
import { adjustTimestampForDisplay, adjustDateForAPI, formatDateTime } from '../utils/dateUtils';
import { Sensor } from '../types';
import { LiveDataReading, SensorReading } from '../types/sensorData';
import { Dialog } from '../components/common/Dialog';

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend, Filler);

// ─── Types ───────────────────────────────────────────────────────────────────

type ReportType =
  | 'summary'
  | 'history'
  | 'history_charts'
  | 'zone'
  | 'alarms'
  | 'minmax'
  | 'audit'
  | 'config';

interface DatePreset { label: string; hours: number }
const DATE_PRESETS: DatePreset[] = [
  { label: '24 hours', hours: 24 },
  { label: '7 days', hours: 168 },
  { label: '30 days', hours: 720 },
];

interface BucketPreset {
  label: string;
  minutes: number;
}
const BUCKET_PRESETS: BucketPreset[] = [
  { label: '1 minute', minutes: 1 },
  { label: '5 minutes', minutes: 5 },
  { label: '15 minutes', minutes: 15 },
  { label: '30 minutes', minutes: 30 },
  { label: '1 hour', minutes: 60 },
  { label: '2 hours', minutes: 120 },
  { label: '24 hours', minutes: 1440 },
];

const MAX_CHART_BUCKET_WARN = 400;
const MAX_CHART_POINTS_HARD_CAP = 500;
/** History report bucket table row cap — protects the PDF capture from pathological
 *  combinations (e.g. 30 days at 1-minute granularity = ~43k rows). 7d/1h = 168 rows, well under. */
const MAX_TABLE_ROWS = 750;

const REPORT_TYPE_LABELS: Partial<Record<ReportType, string>> = {
  summary: 'Sensor-Summary',
  history: 'Temperature-History',
  history_charts: 'Temperature-Chart-Pages',
  zone: 'Zone-Summary',
  alarms: 'Alarm-Report',
  minmax: 'MinMax-Summary',
  audit: 'Audit-Log',
  config: 'System-Config',
};

// ─── Helpers ──────────────────────────────────────────────────────────────────

function sensorStatus(liveReading: LiveDataReading | undefined): 'live' | 'stale' | 'offline' {
  if (!liveReading?.timestamp) return 'offline';
  const mins = (Date.now() - new Date(liveReading.timestamp).getTime()) / 60000;
  if (mins < 90) return 'live';
  if (mins < 240) return 'stale';
  return 'offline';
}

function formatDurationLong(ms: number): string {
  if (!ms || isNaN(ms) || ms < 0) return '—';
  const totalSec = Math.floor(ms / 1000);
  const days  = Math.floor(totalSec / 86400);
  const hours = Math.floor((totalSec % 86400) / 3600);
  const mins  = Math.floor((totalSec % 3600) / 60);
  if (days  > 0) return `${days}d ${hours}h`;
  if (hours > 0) return `${hours}h ${mins}m`;
  if (mins  > 0) return `${mins}m`;
  return `${totalSec}s`;
}

function bucketStartMs(displayDate: Date, bucketMinutes: number): number {
  const ms = bucketMinutes * 60 * 1000;
  const t = displayDate.getTime();
  return Math.floor(t / ms) * ms;
}

function formatBucketLabel(bucketStart: Date, bucketMinutes: number): string {
  if (bucketMinutes >= 1440) return format(bucketStart, 'dd/MM/yyyy');
  if (bucketMinutes >= 60) return format(bucketStart, 'dd/MM yyyy HH:00');
  return format(bucketStart, 'dd/MM HH:mm');
}

function aggregateReadingsIntoBuckets(
  readings: { timestamp: string; temperature: number }[],
  bucketMinutes: number
): { label: string; avg: number; bucketStartMs: number }[] {
  const bucketMs = bucketMinutes * 60 * 1000;
  const map = new Map<number, number[]>();
  for (const r of readings) {
    const adj = adjustTimestampForDisplay(r.timestamp);
    const startMs = bucketStartMs(adj, bucketMinutes);
    if (!map.has(startMs)) map.set(startMs, []);
    map.get(startMs)!.push(r.temperature);
  }
  const keys = Array.from(map.keys()).sort((a, b) => a - b);
  return keys.map(k => {
    const vals = map.get(k)!;
    const avg = parseFloat((vals.reduce((a, b) => a + b, 0) / vals.length).toFixed(2));
    return {
      label: formatBucketLabel(new Date(k), bucketMinutes),
      avg,
      bucketStartMs: k,
    };
  });
}

/** Downsample contiguous bucket series for Chart.js — max `maxPoints` evenly spaced indices */
function capSeriesForChart<T extends { avg: number; label: string }>(
  buckets: T[],
  maxPoints: number
): T[] {
  if (buckets.length <= maxPoints) return buckets;
  const step = buckets.length / maxPoints;
  const out: T[] = [];
  for (let i = 0; i < maxPoints; i++) {
    const idx = Math.min(buckets.length - 1, Math.floor(i * step));
    out.push(buckets[idx]!);
  }
  return out;
}

function StatusBadge({ status }: { status: 'live' | 'stale' | 'offline' }) {
  const cfg = {
    live:    { cls: 'bg-green-100 text-green-800',  icon: <CheckCircle className="w-3 h-3" />,  label: 'Live' },
    stale:   { cls: 'bg-yellow-100 text-yellow-800', icon: <AlertTriangle className="w-3 h-3" />, label: 'Stale' },
    offline: { cls: 'bg-red-100 text-red-800',      icon: <WifiOff className="w-3 h-3" />,       label: 'Offline' },
  }[status];
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${cfg.cls}`}>
      {cfg.icon}{cfg.label}
    </span>
  );
}

// ─── PDF export ───────────────────────────────────────────────────────────────

interface PDFBranding {
  companyName?: string;
  logoUrl?: string;
  phone?: string;
  website?: string;
  emailAddress?: string;
}

function pdfImageFormat(dataUrl: string): 'PNG' | 'JPEG' | null {
  if (dataUrl.startsWith('data:image/png')) return 'PNG';
  if (dataUrl.startsWith('data:image/jpeg') || dataUrl.startsWith('data:image/jpg')) return 'JPEG';
  return null;
}

const PDF_CAPTURE_OPTS = { scale: 2, useCORS: true, backgroundColor: '#ffffff' } as const;

/**
 * html2canvas clones the page into an off-screen iframe sized to the current browser window by
 * default, so a captured element taller than the visible viewport can get cropped at capture time —
 * independent of the PDF page-fitting logic below, which never crops. Only windowHeight is widened
 * here: inflating windowWidth as well caused captured sections to render narrower than their actual
 * pinned width (some ancestor reflowed differently at the wider clone viewport), leaving a blank
 * strip on the right — so width is left at the real viewport width.
 */
function captureNodeCanvas(node: HTMLElement): Promise<HTMLCanvasElement> {
  return html2canvas(node, {
    ...PDF_CAPTURE_OPTS,
    windowHeight: Math.max(window.innerHeight, node.scrollHeight + 50),
  });
}

/**
 * Forcing a node's width wider for export (so it captures at full report width instead of the
 * narrower on-screen preview width) is a synchronous DOM mutation, but Chart.js only redraws its
 * canvas in response to a ResizeObserver callback, which fires on a later frame. Calling
 * html2canvas immediately after resizing captures the chart's *old*, narrower canvas bitmap while
 * the surrounding plain HTML (which reflows synchronously) already shows the new width — exactly
 * the "chart doesn't fill the page" symptom. Waiting two animation frames gives the resize
 * observer + Chart.js's redraw time to complete before the snapshot is taken.
 */
function waitForLayout(): Promise<void> {
  return new Promise(resolve => requestAnimationFrame(() => requestAnimationFrame(() => resolve())));
}

const PDF_LAYOUT = {
  HEADER_H: 18,
  FOOTER_H: 12,
  MARGIN: 10,
} as const;

/** How many portrait PDF pages one canvas occupies (given same layout as appendCanvasSlicesToPdf). */
function countSlicePages(canvas: HTMLCanvasElement, pdf: jsPDF): number {
  const pageW = pdf.internal.pageSize.getWidth();
  const pageH = pdf.internal.pageSize.getHeight();
  const { HEADER_H, FOOTER_H, MARGIN } = PDF_LAYOUT;
  const contentW = pageW - 2 * MARGIN;
  const contentH = pageH - HEADER_H - FOOTER_H;
  const pxPerMm = canvas.width / contentW;
  const pxPerPage = contentH * pxPerMm;
  return Math.ceil(canvas.height / pxPerPage);
}

function appendCanvasSlicesToPdf(
  pdf: jsPDF,
  canvas: HTMLCanvasElement,
  branding?: PDFBranding,
  generatedAt?: Date,
  pageNumbering?: { pageNumStart: number; totalPagesLabel: number },
) {
  const pageW = pdf.internal.pageSize.getWidth();
  const pageH = pdf.internal.pageSize.getHeight();

  const { HEADER_H, FOOTER_H, MARGIN } = PDF_LAYOUT;
  const contentW = pageW - 2 * MARGIN;
  const contentH = pageH - HEADER_H - FOOTER_H;

  const pxPerMm = canvas.width / contentW;
  const pxPerPage = contentH * pxPerMm;
  const totalPages = Math.ceil(canvas.height / pxPerPage);

  const drawHeader = (pageNum: number) => {
    pdf.setFillColor(248, 249, 250);
    pdf.rect(0, 0, pageW, HEADER_H, 'F');
    pdf.setDrawColor(209, 213, 219);
    pdf.line(0, HEADER_H, pageW, HEADER_H);

    let textX = MARGIN;

    if (branding?.logoUrl) {
      const fmt = pdfImageFormat(branding.logoUrl);
      if (fmt) {
        try {
          pdf.addImage(branding.logoUrl, fmt, MARGIN, 2, 0, 14);
          textX = MARGIN + 34;
        } catch {
          //
        }
      }
    }

    if (branding?.companyName) {
      pdf.setFontSize(10);
      pdf.setFont('helvetica', 'bold');
      pdf.setTextColor(30, 30, 30);
      pdf.text(branding.companyName, textX, 11);
    }

    pdf.setFontSize(8);
    pdf.setFont('helvetica', 'normal');
    pdf.setTextColor(100, 100, 100);
    const labelTotal = pageNumbering?.totalPagesLabel ?? totalPages;
    const labelPage = pageNumbering ? pageNumbering.pageNumStart + pageNum - 1 : pageNum;
    pdf.text(`Page ${labelPage} of ${labelTotal}`, pageW - MARGIN, 11, { align: 'right' });
  };

  const drawFooter = () => {
    const y = pageH - FOOTER_H;
    pdf.setDrawColor(209, 213, 219);
    pdf.line(MARGIN, y, pageW - MARGIN, y);
    pdf.setFillColor(248, 249, 250);
    pdf.rect(0, y, pageW, FOOTER_H, 'F');

    pdf.setFontSize(7);
    pdf.setFont('helvetica', 'normal');
    pdf.setTextColor(100, 100, 100);

    const parts: string[] = [];
    if (branding?.phone) parts.push(branding.phone);
    if (branding?.emailAddress) parts.push(branding.emailAddress);
    if (branding?.website) parts.push(branding.website);
    if (parts.length > 0) {
      pdf.text(parts.join('  ·  '), MARGIN, y + 5);
    }

    if (generatedAt) {
      pdf.text(
        `Generated: ${format(generatedAt, 'dd MMM yyyy HH:mm')}`,
        pageW - MARGIN,
        y + 5,
        { align: 'right' },
      );
    }
  };

  for (let pageNum = 1; pageNum <= totalPages; pageNum++) {
    if (pageNum > 1) pdf.addPage();

    const offsetPx = (pageNum - 1) * pxPerPage;
    const sliceH = Math.min(pxPerPage, canvas.height - offsetPx);

    const sliceCanvas = document.createElement('canvas');
    sliceCanvas.width = canvas.width;
    sliceCanvas.height = sliceH;
    sliceCanvas.getContext('2d')!.drawImage(canvas, 0, -offsetPx);

    pdf.addImage(
      sliceCanvas.toDataURL('image/png'), 'PNG',
      MARGIN, HEADER_H,
      contentW, sliceH / pxPerMm,
    );

    drawHeader(pageNum);
    drawFooter();
  }
}

async function exportHistoryReportPdf(
  root: HTMLElement,
  filename: string,
  branding?: PDFBranding,
  generatedAt?: Date,
  opts: { download?: boolean } = {},
): Promise<jsPDF> {
  const download = opts.download ?? true;
  const sections = root.querySelectorAll<HTMLElement>('[data-history-pdf-section]');

  const pdf = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });

  if (sections.length === 0) {
    const canvas = await captureNodeCanvas(root);
    appendCanvasSlicesToPdf(pdf, canvas, branding, generatedAt);
    if (download) pdf.save(filename);
    return pdf;
  }

  const canvases: HTMLCanvasElement[] = [];

  for (let i = 0; i < sections.length; i++) {
    const node = sections[i];
    const prevMaxH = node.style.maxHeight;
    const prevOv = node.style.overflow;
    const prevW = node.style.width;
    const prevMinW = node.style.minWidth;

    node.style.maxHeight = 'none';
    node.style.overflow = 'visible';
    node.style.width = '1400px';
    node.style.minWidth = '1400px';
    await waitForLayout();

    try {
      canvases.push(await captureNodeCanvas(node));
    } finally {
      node.style.maxHeight = prevMaxH;
      node.style.overflow = prevOv;
      node.style.width = prevW;
      node.style.minWidth = prevMinW;
    }
  }

  const globalTotalPages = canvases.reduce((sum, c) => sum + countSlicePages(c, pdf), 0);
  let pageNumStart = 1;

  for (let i = 0; i < canvases.length; i++) {
    if (i > 0) pdf.addPage();
    const fragPages = countSlicePages(canvases[i], pdf);
    appendCanvasSlicesToPdf(pdf, canvases[i], branding, generatedAt, {
      pageNumStart,
      totalPagesLabel: globalTotalPages,
    });
    pageNumStart += fragPages;
  }

  if (download) pdf.save(filename);
  return pdf;
}

/** One PDF page per capture: scale image into content area below header/footer (works for portrait or landscape). */
function appendFittedCanvasPage(
  pdf: jsPDF,
  canvas: HTMLCanvasElement,
  branding: PDFBranding | undefined,
  generatedAt: Date | undefined,
  pageNumbering: { pageNum: number; totalPages: number },
) {
  const pageW = pdf.internal.pageSize.getWidth();
  const pageH = pdf.internal.pageSize.getHeight();
  const { HEADER_H, FOOTER_H, MARGIN } = PDF_LAYOUT;
  const contentW = pageW - 2 * MARGIN;
  const contentH = pageH - HEADER_H - FOOTER_H;

  const imgAspect = canvas.width / Math.max(1, canvas.height);
  let drawW = contentW;
  let drawH = drawW / imgAspect;
  if (drawH > contentH) {
    drawH = contentH;
    drawW = drawH * imgAspect;
  }
  const x = MARGIN + (contentW - drawW) / 2;
  const y = HEADER_H + (contentH - drawH) / 2;

  pdf.addImage(canvas.toDataURL('image/png'), 'PNG', x, y, drawW, drawH);

  const { pageNum, totalPages } = pageNumbering;

  pdf.setFillColor(248, 249, 250);
  pdf.rect(0, 0, pageW, HEADER_H, 'F');
  pdf.setDrawColor(209, 213, 219);
  pdf.line(0, HEADER_H, pageW, HEADER_H);

  let textX = MARGIN;
  if (branding?.logoUrl) {
    const fmt = pdfImageFormat(branding.logoUrl);
    if (fmt) {
      try {
        pdf.addImage(branding.logoUrl, fmt, MARGIN, 2, 0, 14);
        textX = MARGIN + 34;
      } catch {
        //
      }
    }
  }

  if (branding?.companyName) {
    pdf.setFontSize(10);
    pdf.setFont('helvetica', 'bold');
    pdf.setTextColor(30, 30, 30);
    pdf.text(branding.companyName, textX, 11);
  }

  pdf.setFontSize(8);
  pdf.setFont('helvetica', 'normal');
  pdf.setTextColor(100, 100, 100);
  pdf.text(`Page ${pageNum} of ${totalPages}`, pageW - MARGIN, 11, { align: 'right' });

  const fy = pageH - FOOTER_H;
  pdf.setDrawColor(209, 213, 219);
  pdf.line(MARGIN, fy, pageW - MARGIN, fy);
  pdf.setFillColor(248, 249, 250);
  pdf.rect(0, fy, pageW, FOOTER_H, 'F');
  pdf.setFontSize(7);
  pdf.setFont('helvetica', 'normal');
  pdf.setTextColor(100, 100, 100);
  const parts: string[] = [];
  if (branding?.phone) parts.push(branding.phone);
  if (branding?.emailAddress) parts.push(branding.emailAddress);
  if (branding?.website) parts.push(branding.website);
  if (parts.length > 0) {
    pdf.text(parts.join('  ·  '), MARGIN, fy + 5);
  }
  if (generatedAt) {
    pdf.text(`Generated: ${format(generatedAt, 'dd MMM yyyy HH:mm')}`, pageW - MARGIN, fy + 5, { align: 'right' });
  }
}

async function exportSensorChartPagesPdf(
  root: HTMLElement,
  filename: string,
  branding?: PDFBranding,
  generatedAt?: Date,
  orientation: 'portrait' | 'landscape' = 'landscape',
  opts: { download?: boolean } = {},
): Promise<jsPDF> {
  const download = opts.download ?? true;
  const sections = root.querySelectorAll<HTMLElement>('[data-sensor-chart-pdf-section]');
  const pdf = new jsPDF({ orientation, unit: 'mm', format: 'a4' });
  const totalPages = Math.max(1, sections.length);
  const brandOpts = branding;
  const genAt = generatedAt;

  if (sections.length === 0) {
    const canvas = await captureNodeCanvas(root);
    appendFittedCanvasPage(pdf, canvas, brandOpts, genAt, { pageNum: 1, totalPages: 1 });
    if (download) pdf.save(filename);
    return pdf;
  }

  for (let i = 0; i < sections.length; i++) {
    const node = sections[i];
    const prevMaxH = node.style.maxHeight;
    const prevOv = node.style.overflow;
    const prevW = node.style.width;
    const prevMinW = node.style.minWidth;

    node.style.maxHeight = 'none';
    node.style.overflow = 'visible';
    node.style.width = '1200px';
    node.style.minWidth = '1200px';
    await waitForLayout();

    try {
      const canvas = await captureNodeCanvas(node);
      if (i > 0) pdf.addPage('a4', orientation);
      appendFittedCanvasPage(pdf, canvas, brandOpts, genAt, { pageNum: i + 1, totalPages });
    } finally {
      node.style.maxHeight = prevMaxH;
      node.style.overflow = prevOv;
      node.style.width = prevW;
      node.style.minWidth = prevMinW;
    }
  }

  if (download) pdf.save(filename);
  return pdf;
}

async function exportToPDF(
  el: HTMLElement,
  filename: string,
  branding?: PDFBranding,
  generatedAt?: Date,
  opts: { download?: boolean } = {},
): Promise<jsPDF> {
  const download = opts.download ?? true;
  const canvas = await captureNodeCanvas(el);
  const pdf = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
  appendCanvasSlicesToPdf(pdf, canvas, branding, generatedAt);
  if (download) pdf.save(filename);
  return pdf;
}

// ─── Report header (shared across all report types) ───────────────────────────

interface ReportHeaderProps {
  title: string;
  subtitle: string;
  generatedAt: Date;
  brandName?: string;
  logoUrl?: string;
}

const ReportHeader: React.FC<ReportHeaderProps> = ({ title, subtitle, generatedAt, brandName, logoUrl }) => (
  <div className="border-b-2 border-gray-800 pb-4 mb-6">
    <div className="flex items-start justify-between">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">{title}</h1>
        <p className="text-gray-800 mt-1">{subtitle}</p>
      </div>
      <div className="text-right text-sm text-gray-800 flex flex-col items-end gap-1">
        {logoUrl ? (
          <img src={logoUrl} alt="Logo" style={{ maxHeight: 40, maxWidth: 120, objectFit: 'contain' }} />
        ) : null}
        <p className="font-medium">{brandName || 'Temperature Monitoring'}</p>
        <p>Generated: {format(generatedAt, 'dd MMM yyyy HH:mm')}</p>
      </div>
    </div>
  </div>
);

// ─── Summary report ───────────────────────────────────────────────────────────

const SummaryReport: React.FC<{
  sensors: Sensor[];
  liveData: LiveDataReading[];
  generatedAt: Date;
  brandName?: string;
  logoUrl?: string;
}> = ({ sensors, liveData, generatedAt, brandName, logoUrl }) => {
  const liveMap = new Map(liveData.map(r => [r.sensor_id, r]));
  const active = sensors.filter(s => !s.is_deleted);

  const liveCount = active.filter(s => sensorStatus(liveMap.get(String(s.serialNo))) === 'live').length;
  const temps = liveData.map(r => r.temperature).filter((t): t is number => t !== null);
  const avgTemp = temps.length ? temps.reduce((a, b) => a + b, 0) / temps.length : null;

  return (
    <div className="bg-white text-gray-900 p-8 font-sans">
      <ReportHeader
        title="Sensor Status Summary"
        subtitle={`Snapshot of all ${active.length} sensors`}
        generatedAt={generatedAt}
        brandName={brandName}
        logoUrl={logoUrl}
      />

      {/* Stats row */}
      <div className="grid grid-cols-3 gap-4 mb-8">
        <div className="border border-gray-200 rounded p-4 text-center">
          <p className="text-3xl font-bold text-green-600">{liveCount}</p>
          <p className="text-sm text-gray-700 mt-1">Live sensors</p>
        </div>
        <div className="border border-gray-200 rounded p-4 text-center">
          <p className="text-3xl font-bold text-gray-800">{active.length}</p>
          <p className="text-sm text-gray-700 mt-1">Total sensors</p>
        </div>
        <div className="border border-gray-200 rounded p-4 text-center">
          <p className="text-3xl font-bold text-blue-600">{avgTemp !== null ? `${avgTemp.toFixed(1)}°C` : '—'}</p>
          <p className="text-sm text-gray-700 mt-1">Average temperature</p>
        </div>
      </div>

      {/* Sensor table */}
      <table className="w-full text-sm border-collapse">
        <thead>
          <tr className="bg-gray-100">
            <th className="border border-gray-200 px-3 py-2 text-left">Sensor</th>
            <th className="border border-gray-200 px-3 py-2 text-left">Serial</th>
            <th className="border border-gray-200 px-3 py-2 text-left">Status</th>
            <th className="border border-gray-200 px-3 py-2 text-right">Temp (°C)</th>
            <th className="border border-gray-200 px-3 py-2 text-right">Battery</th>
            <th className="border border-gray-200 px-3 py-2 text-right">Signal</th>
            <th className="border border-gray-200 px-3 py-2 text-left">Last Seen</th>
          </tr>
        </thead>
        <tbody>
          {active.map((sensor, i) => {
            const reading = liveMap.get(String(sensor.serialNo));
            const status = sensorStatus(reading);
            return (
              <tr key={sensor.id} className={i % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                <td className="border border-gray-200 px-3 py-2 font-medium">{sensor.name}</td>
                <td className="border border-gray-200 px-3 py-2 font-mono text-xs">{sensor.serialNo}</td>
                <td className="border border-gray-200 px-3 py-2">
                  <StatusBadge status={status} />
                </td>
                <td className="border border-gray-200 px-3 py-2 text-right">
                  {reading?.temperature != null ? reading.temperature.toFixed(2) : '—'}
                </td>
                <td className="border border-gray-200 px-3 py-2 text-right">{reading?.Battery ?? '—'}</td>
                <td className="border border-gray-200 px-3 py-2 text-right">
                  {reading?.RSSI != null ? `${reading.RSSI}%` : '—'}
                </td>
                <td className="border border-gray-200 px-3 py-2 text-xs text-gray-800">
                  {reading?.timestamp ? formatDateTime(adjustTimestampForDisplay(reading.timestamp)) : '—'}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

// ─── History report (per-sensor) ──────────────────────────────────────────────

const historyBucketLabelText = (bucketMinutes: number) =>
  bucketMinutes >= 1440 ? '1 day' : bucketMinutes >= 60 ? `${bucketMinutes / 60}h` : `${bucketMinutes}m`;

const SensorHistorySection: React.FC<{
  sensor: Sensor;
  startDate: string;
  endDate: string;
  bucketMinutes: number;
  /** full: table + default chart height; chartPdf: large chart, no bucket table (optimized for one PDF page per sensor) */
  layout?: 'full' | 'chartPdf';
  /** chartPdf only — sizes the chart so the captured card's aspect ratio fills the target page instead of leaving it letterboxed. */
  pdfOrientation?: 'portrait' | 'landscape';
}> = ({ sensor, startDate, endDate, bucketMinutes, layout = 'full', pdfOrientation = 'landscape' }) => {
  const { data: readings = [], isLoading } = useGetSensorReadingsQuery({
    sensorId: sensor.id,
    startDate,
    endDate,
    bucketMinutes,
  });

  const bucketEstimate = estimateBucketCountMs(startDate, endDate, bucketMinutes);

  if (isLoading) return (
    <div className="mb-8 border border-gray-200 rounded p-6 flex items-center gap-2 text-gray-800">
      <Loader2 className="w-4 h-4 animate-spin" /> Loading {sensor.name}…
    </div>
  );

  const validReadings = readings
    .filter((r): r is typeof r & { temperature: number } => r.temperature !== null && r.temperature !== undefined);

  const temps = validReadings.map(r => r.temperature);
  const minTemp = temps.length ? Math.min(...temps) : null;
  const maxTemp = temps.length ? Math.max(...temps) : null;
  const avgTemp = temps.length ? temps.reduce((a, b) => a + b, 0) / temps.length : null;

  const bucketRows = aggregateReadingsIntoBuckets(
    validReadings.map(r => ({ timestamp: r.timestamp, temperature: r.temperature })),
    bucketMinutes
  );
  const chartSeries =
    bucketRows.length > MAX_CHART_POINTS_HARD_CAP
      ? capSeriesForChart(bucketRows, MAX_CHART_POINTS_HARD_CAP)
      : bucketRows;

  const chartData = {
    labels: chartSeries.map(b => b.label),
    datasets: [{
      label: 'Avg Temperature (°C)',
      data: chartSeries.map(b => b.avg),
      borderColor: '#3b82f6',
      backgroundColor: 'rgba(59,130,246,0.1)',
      borderWidth: 2,
      pointRadius: chartSeries.length > 48 ? 0 : 3,
      fill: true,
      tension: 0.3,
    }],
  };

  // Captured at a fixed 1200px width and fitted onto an A4 page without stretching — a short chart
  // on a portrait page leaves huge top/bottom margins, so size the chart to roughly match the
  // page's aspect ratio (minus the fixed-height header/stats above it) instead of a flat 360px.
  const chartPdfHeight = pdfOrientation === 'portrait' ? 1380 : 480;

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: layout !== 'chartPdf',
    // Pin to 1 so the canvas's backing bitmap matches its CSS size exactly — leaving Chart.js's
    // default devicePixelRatio (2 on HiDPI screens) lets it compound with html2canvas's own
    // scale:2 capture option, which renders the chart at roughly half its real width on export.
    devicePixelRatio: 1,
    plugins: { legend: { display: false }, tooltip: { mode: 'index' as const } },
    scales: {
      x: {
        ticks: {
          maxRotation: 45,
          font: { size: layout === 'chartPdf' ? 10 : 9 },
        },
      },
      y: {
        title: { display: true, text: '°C', font: { size: layout === 'chartPdf' ? 11 : undefined } },
        ticks: layout === 'chartPdf' ? { font: { size: 10 } } : {},
      },
    },
  };

  const periodLine = `${format(new Date(startDate), 'dd MMM yyyy HH:mm')} → ${format(new Date(endDate), 'dd MMM yyyy HH:mm')} · Bucket: ${historyBucketLabelText(bucketMinutes)}`;

  return (
    <div className={`${layout === 'full' ? 'mb-8' : ''} border border-gray-200 rounded p-6 break-inside-avoid bg-white`}>
      <h3 className={`font-bold text-gray-900 ${layout === 'chartPdf' ? 'text-lg' : 'text-base'} mb-1`}>{sensor.name}</h3>
      <p className="text-xs text-gray-800 font-mono mb-1">Serial: {sensor.serialNo}</p>
      {layout === 'chartPdf' && (
        <p className="text-xs text-gray-600 mb-4">{periodLine}</p>
      )}

      {bucketEstimate > MAX_CHART_BUCKET_WARN && (
        <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1.5 mb-3">
          Many time buckets ({bucketEstimate.toLocaleString()}). Chart shows up to {MAX_CHART_POINTS_HARD_CAP} spaced points — widen the granularity for finer detail over the full period.
        </p>
      )}

      {/* Stats */}
      <div className="grid grid-cols-3 gap-3 mb-4">
        {[
          { label: 'Min', val: minTemp, color: 'text-blue-600' },
          { label: 'Avg', val: avgTemp, color: 'text-gray-800' },
          { label: 'Max', val: maxTemp, color: 'text-red-600' },
        ].map(({ label, val, color }) => (
          <div key={label} className="bg-gray-50 border border-gray-200 rounded p-3 text-center">
            <p className={`text-xl font-bold ${color}`}>{val !== null ? `${val.toFixed(1)}°C` : '—'}</p>
            <p className="text-xs text-gray-800">{label} temperature</p>
          </div>
        ))}
      </div>

      {/* Chart */}
      {bucketRows.length > 0 ? (
        <div className={layout === 'chartPdf' ? 'mb-2' : 'mb-4'} style={layout === 'chartPdf' ? { height: chartPdfHeight } : undefined}>
          <Line data={chartData} options={chartOptions} height={layout === 'chartPdf' ? chartPdfHeight : 80} />
        </div>
      ) : (
        <p className="text-gray-800 text-sm text-center py-4">No data in this period</p>
      )}

      {/* Table — full selected range (capped only to keep the PDF capture manageable) */}
      {layout === 'full' && bucketRows.length > 0 && (
        <>
          {bucketRows.length > MAX_TABLE_ROWS && (
            <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1.5 mt-2">
              Table shows the most recent {MAX_TABLE_ROWS.toLocaleString()} of {bucketRows.length.toLocaleString()} periods — choose a coarser granularity to cover the full range, or use Export CSV for every period.
            </p>
          )}
          <table className="w-full text-xs border-collapse mt-2">
            <thead>
              <tr className="bg-gray-100">
                <th className="border border-gray-200 px-2 py-1 text-left">
                  Period ({bucketMinutes >= 1440 ? 'day' : bucketMinutes >= 60 ? 'hour+' : String(bucketMinutes).concat('m')})
                </th>
                <th className="border border-gray-200 px-2 py-1 text-right">Avg Temp (°C)</th>
              </tr>
            </thead>
            <tbody>
              {bucketRows.slice(-MAX_TABLE_ROWS).map((row, i) => (
                <tr key={`${row.bucketStartMs}-${i}`} className={i % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                  <td className="border border-gray-200 px-2 py-1">{row.label}</td>
                  <td className="border border-gray-200 px-2 py-1 text-right">{row.avg.toFixed(2)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}
    </div>
  );
};

const HistoryChartsPdfReport: React.FC<{
  sensors: Sensor[];
  startDate: string;
  endDate: string;
  bucketMinutes: number;
  pdfOrientation: 'portrait' | 'landscape';
}> = ({ sensors, startDate, endDate, bucketMinutes, pdfOrientation }) => (
  <div className="bg-white text-gray-900 p-6 font-sans">
    <p className="text-xs text-gray-500 mb-4">
      Temperature chart pages — Export PDF uses {pdfOrientation === 'landscape' ? 'Landscape' : 'Portrait'} A4 with one graph per page. Change PDF orientation in the panel.
    </p>
    {sensors.map(s => (
      <div key={s.id} data-sensor-chart-pdf-section className="mb-6 last:mb-0">
        <SensorHistorySection
          sensor={s}
          startDate={startDate}
          endDate={endDate}
          bucketMinutes={bucketMinutes}
          layout="chartPdf"
          pdfOrientation={pdfOrientation}
        />
      </div>
    ))}
  </div>
);

const HistoryReport: React.FC<{
  sensors: Sensor[];
  startDate: string;
  endDate: string;
  generatedAt: Date;
  bucketMinutes: number;
  brandName?: string;
  logoUrl?: string;
}> = ({ sensors, startDate, endDate, generatedAt, bucketMinutes, brandName, logoUrl }) => {
  const subtitle = `${format(new Date(startDate), 'dd MMM yyyy HH:mm')} → ${format(new Date(endDate), 'dd MMM yyyy HH:mm')} · Bucket: ${historyBucketLabelText(bucketMinutes)}`;
  const header = (
    <ReportHeader
      title="Temperature History Report"
      subtitle={subtitle}
      generatedAt={generatedAt}
      brandName={brandName}
      logoUrl={logoUrl}
    />
  );

  return (
    <div className="bg-white text-gray-900 p-8 font-sans">
      {sensors.length > 0 ? (
        <>
          <div data-history-pdf-section className="break-inside-avoid">
            {header}
            <SensorHistorySection sensor={sensors[0]} startDate={startDate} endDate={endDate} bucketMinutes={bucketMinutes} />
          </div>
          {sensors.slice(1).map(s => (
            <div key={s.id} data-history-pdf-section className="break-inside-avoid">
              <SensorHistorySection sensor={s} startDate={startDate} endDate={endDate} bucketMinutes={bucketMinutes} />
            </div>
          ))}
        </>
      ) : (
        <div data-history-pdf-section className="break-inside-avoid">{header}</div>
      )}
    </div>
  );
};

// ─── Zone report ──────────────────────────────────────────────────────────────

const ZoneReport: React.FC<{
  sensors: Sensor[];
  liveData: LiveDataReading[];
  generatedAt: Date;
  brandName?: string;
  logoUrl?: string;
}> = ({ sensors, liveData, generatedAt, brandName, logoUrl }) => {
  const { data: zones = [] } = useGetZonesQuery();
  const liveMap = new Map(liveData.map(r => [r.sensor_id, r]));
  const serialNoById = useMemo(() => new Map(sensors.map(s => [s.id, s.serialNo])), [sensors]);

  return (
    <div className="bg-white text-gray-900 p-8 font-sans">
      <ReportHeader
        title="Zone Summary Report"
        subtitle={`${zones.filter(z => !z.is_deleted).length} active zones`}
        generatedAt={generatedAt}
        brandName={brandName}
        logoUrl={logoUrl}
      />
      {zones.filter(z => !z.is_deleted).map(zone => (
        <ZoneSection key={zone.zone_id} zone={zone} liveMap={liveMap} serialNoById={serialNoById} />
      ))}
    </div>
  );
};

const ZoneSection: React.FC<{
  zone: { zone_id: number; zone_name: string; sensor_count: number };
  liveMap: Map<string, LiveDataReading>;
  serialNoById: Map<number, number | string>;
}> = ({ zone, liveMap, serialNoById }) => {
  const { data: zoneSensors = [] } = useGetZoneSensorsQuery(zone.zone_id);

  return (
    <div className="mb-6 border border-gray-200 rounded p-5 break-inside-avoid">
      <div className="flex items-center justify-between mb-3">
        <h3 className="font-bold text-gray-900">{zone.zone_name}</h3>
        <span className="text-xs text-gray-800">{zone.sensor_count} sensor{zone.sensor_count !== 1 ? 's' : ''}</span>
      </div>
      <table className="w-full text-sm border-collapse">
        <thead>
          <tr className="bg-gray-100">
            <th className="border border-gray-200 px-3 py-2 text-left">Sensor</th>
            <th className="border border-gray-200 px-3 py-2 text-left">Status</th>
            <th className="border border-gray-200 px-3 py-2 text-right">Temp (°C)</th>
            <th className="border border-gray-200 px-3 py-2 text-left">Last Seen</th>
          </tr>
        </thead>
        <tbody>
          {zoneSensors.map((zs, i) => {
            const serialNo = zs.sensor_serialNo ?? serialNoById.get(zs.sensor_id) ?? null;
            const reading = serialNo != null ? liveMap.get(String(serialNo)) : undefined;
            const status = sensorStatus(reading);
            return (
              <tr key={zs.sensor_id} className={i % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                <td className="border border-gray-200 px-3 py-2">{zs.sensor_name}</td>
                <td className="border border-gray-200 px-3 py-2"><StatusBadge status={status} /></td>
                <td className="border border-gray-200 px-3 py-2 text-right">
                  {reading?.temperature != null ? reading.temperature.toFixed(2) : '—'}
                </td>
                <td className="border border-gray-200 px-3 py-2 text-xs text-gray-800">
                  {reading?.timestamp ? formatDateTime(adjustTimestampForDisplay(reading.timestamp)) : '—'}
                </td>
              </tr>
            );
          })}
          {zoneSensors.length === 0 && (
            <tr><td colSpan={4} className="border border-gray-200 px-3 py-2 text-center text-gray-800 text-sm">No sensors in this zone</td></tr>
          )}
        </tbody>
      </table>
    </div>
  );
};

// ─── Min / Max summary ────────────────────────────────────────────────────────

/** For ranges beyond ~25h the API returns bucket rows: `temperature` is the bucket avg; true extremes are temp_min/temp_max. */
function computeTemperatureMinMaxAvg(readings: SensorReading[]): {
  minV: number | null;
  minTs: string | null;
  maxV: number | null;
  maxTs: string | null;
  avg: number | null;
} {
  let minV: number | null = null;
  let minTs: string | null = null;
  let maxV: number | null = null;
  let maxTs: string | null = null;
  let weightedSum = 0;
  let weightedCount = 0;

  for (const r of readings) {
    const avgT = r.temperature;
    const lo =
      r.temp_min != null && Number.isFinite(r.temp_min) ? r.temp_min : avgT;
    const hi =
      r.temp_max != null && Number.isFinite(r.temp_max) ? r.temp_max : avgT;

    if (lo == null || hi == null || !Number.isFinite(lo) || !Number.isFinite(hi)) continue;

    if (minV === null || lo < minV) {
      minV = lo;
      minTs = r.timestamp;
    }
    if (maxV === null || hi > maxV) {
      maxV = hi;
      maxTs = r.timestamp;
    }

    if (avgT != null && Number.isFinite(avgT)) {
      const w = typeof r.sample_count === 'number' && r.sample_count > 0 ? r.sample_count : 1;
      weightedSum += avgT * w;
      weightedCount += w;
    }
  }

  return {
    minV,
    minTs,
    maxV,
    maxTs,
    avg: weightedCount > 0 ? weightedSum / weightedCount : null,
  };
}

function computeMKT(readings: SensorReading[]): number | null {
  const DH = 83144; // J/mol — standard activation energy (ASTM E1487)
  const R = 8.314;  // J/(mol·K)
  const temps = readings
    .map(r => r.temperature)
    .filter((t): t is number => t !== null && Number.isFinite(t));
  if (temps.length === 0) return null;
  const n = temps.length;
  const sum = temps.reduce((acc, tc) => acc + Math.exp(-DH / (R * (tc + 273.15))), 0);
  if (sum <= 0) return null;
  const lnA = Math.log(sum / n);
  if (lnA >= 0) return null;
  const mktK = (DH / R) / (-lnA);
  return Number.isFinite(mktK) ? mktK - 273.15 : null;
}

const MinMaxSensorSection: React.FC<{
  sensor: Sensor;
  startDate: string;
  endDate: string;
}> = ({ sensor, startDate, endDate }) => {
  const { data: readings = [], isLoading } = useGetSensorReadingsQuery({
    sensorId: sensor.id,
    startDate,
    endDate,
  });

  if (isLoading) {
    return (
      <tr>
        <td colSpan={8} className="border border-gray-200 px-3 py-2 text-gray-800">
          <Loader2 className="w-4 h-4 animate-spin inline mr-2" />
          Loading {sensor.name}…
        </td>
      </tr>
    );
  }

  const { minV, minTs, maxV, maxTs, avg } = computeTemperatureMinMaxAvg(readings);
  const mkt = computeMKT(readings);

  return (
    <tr>
      <td className="border border-gray-200 px-3 py-2 font-medium">{sensor.name}</td>
      <td className="border border-gray-200 px-3 py-2 font-mono text-xs">{sensor.serialNo}</td>
      <td className="border border-gray-200 px-3 py-2 text-right">
        {minV !== null ? `${minV.toFixed(2)}` : '—'}
      </td>
      <td className="border border-gray-200 px-3 py-2 text-xs text-gray-800">
        {minTs ? format(adjustTimestampForDisplay(minTs), 'dd MMM HH:mm') : '—'}
      </td>
      <td className="border border-gray-200 px-3 py-2 text-right">
        {maxV !== null ? `${maxV.toFixed(2)}` : '—'}
      </td>
      <td className="border border-gray-200 px-3 py-2 text-xs text-gray-800">
        {maxTs ? format(adjustTimestampForDisplay(maxTs), 'dd MMM HH:mm') : '—'}
      </td>
      <td className="border border-gray-200 px-3 py-2 text-right">
        {avg !== null ? `${avg.toFixed(2)}` : '—'}
      </td>
      <td className="border border-gray-200 px-3 py-2 text-right">
        {mkt !== null ? `${mkt.toFixed(2)}` : '—'}
      </td>
    </tr>
  );
};

const MinMaxReport: React.FC<{
  sensors: Sensor[];
  startDate: string;
  endDate: string;
  generatedAt: Date;
  brandName?: string;
  logoUrl?: string;
}> = ({ sensors, startDate, endDate, generatedAt, brandName, logoUrl }) => (
  <div className="bg-white text-gray-900 p-8 font-sans">
    <ReportHeader
      title="Min–Max Temperature Summary"
      subtitle={`${format(new Date(startDate), 'dd MMM yyyy HH:mm')} → ${format(new Date(endDate), 'dd MMM yyyy HH:mm')} · ${sensors.length} sensor(s)`}
      generatedAt={generatedAt}
      brandName={brandName}
      logoUrl={logoUrl}
    />
    <p className="text-xs text-gray-800 mb-3">
      Min, max, and average values are computed from all readings returned for the dates above (including aggregated buckets when the selected range spans more than about a day). MKT uses ΔH = 83,144 J/mol.
    </p>
    <table className="w-full text-sm border-collapse">
      <thead>
        <tr className="bg-gray-100">
          <th className="border border-gray-200 px-3 py-2 text-left">Sensor</th>
          <th className="border border-gray-200 px-3 py-2 text-left">Serial</th>
          <th className="border border-gray-200 px-3 py-2 text-right">Min (°C)</th>
          <th className="border border-gray-200 px-3 py-2 text-left">Min at</th>
          <th className="border border-gray-200 px-3 py-2 text-right">Max (°C)</th>
          <th className="border border-gray-200 px-3 py-2 text-left">Max at</th>
          <th className="border border-gray-200 px-3 py-2 text-right">Avg (°C)</th>
          <th className="border border-gray-200 px-3 py-2 text-right">MKT (°C)</th>
        </tr>
      </thead>
      <tbody>
        {sensors.length === 0 ? (
          <tr>
            <td colSpan={8} className="border border-gray-200 px-3 py-6 text-center text-gray-800">
              No sensors selected
            </td>
          </tr>
        ) : (
          sensors.map(s => (
            <MinMaxSensorSection key={s.id} sensor={s} startDate={startDate} endDate={endDate} />
          ))
        )}
      </tbody>
    </table>
  </div>
);

type AuditLogRow = {
  id: number;
  user_id: string | number;
  action: string;
  description: string;
  timestamp: string;
};

async function fetchAllAuditLogsForReport(startIso: string | undefined, endIso: string | undefined): Promise<AuditLogRow[]> {
  const accumulated: AuditLogRow[] = [];
  const PAGE = 5000;
  let offset = 0;

  while (accumulated.length < 250000) {
    const u = new URL(`${API_CONFIG.baseURL}/audit-logs`);
    u.searchParams.set('limit', String(PAGE));
    u.searchParams.set('offset', String(offset));
    u.searchParams.set('export', 'true');
    if (startIso) u.searchParams.set('startDate', startIso);
    if (endIso) u.searchParams.set('endDate', endIso);

    const res = await fetch(u.toString(), {
      method: 'GET',
      credentials: 'include',
      headers: prepareCommonHeaders(),
    });
    if (!res.ok) throw new Error(res.status === 403 ? 'Admin access required for audit export' : `Audit fetch failed (${res.status})`);

    const batch: AuditLogRow[] = await res.json();
    if (!Array.isArray(batch) || batch.length === 0) break;

    accumulated.push(...batch);

    if (batch.length < PAGE) break;

    offset += PAGE;
  }

  return accumulated;
}

const AuditLogsReport: React.FC<{
  startDate: string;
  endDate: string;
  generatedAt: Date;
  brandName?: string;
  logoUrl?: string;
}> = ({ startDate, endDate, generatedAt, brandName, logoUrl }) => {
  const [rows, setRows] = useState<AuditLogRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const { data: users = [] } = useGetUsersQuery();
  const userNameById = useMemo(
    () => new Map(users.map(u => [u.id, [u.firstName, u.lastName].filter(Boolean).join(' ') || u.email])),
    [users],
  );

  useEffect(() => {
    let cancel = false;
    setRows(null);
    setError(null);

    fetchAllAuditLogsForReport(startDate, endDate)
      .then(data => {
        if (!cancel) setRows(data);
      })
      .catch(err => {
        if (!cancel) setError(err instanceof Error ? err.message : String(err));
      });

    return () => {
      cancel = true;
    };
  }, [startDate, endDate]);

  if (error) {
    return (
      <div className="bg-white text-gray-900 p-8 font-sans">
        <ReportHeader
          title="Activity / Audit Report"
          subtitle="Export failed"
          generatedAt={generatedAt}
          brandName={brandName}
          logoUrl={logoUrl}
        />
        <p className="text-red-600 text-sm">{error}</p>
      </div>
    );
  }

  if (rows === null) {
    return (
      <div className="bg-white text-gray-900 p-8 font-sans">
        <ReportHeader
          title="Activity / Audit Report"
          subtitle={`${format(new Date(startDate), 'dd MMM yyyy HH:mm')} → ${format(new Date(endDate), 'dd MMM yyyy HH:mm')} · Loading…`}
          generatedAt={generatedAt}
          brandName={brandName}
          logoUrl={logoUrl}
        />
        <div className="flex items-center gap-2 text-gray-800">
          <Loader2 className="w-4 h-4 animate-spin" /> Fetching audit log…
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white text-gray-900 p-8 font-sans">
      <ReportHeader
        title="Activity / Audit Report"
        subtitle={`${format(new Date(startDate), 'dd MMM yyyy HH:mm')} → ${format(new Date(endDate), 'dd MMM yyyy HH:mm')} · ${rows.length} row(s)`}
        generatedAt={generatedAt}
        brandName={brandName}
        logoUrl={logoUrl}
      />
      <div className="overflow-x-auto">
        <table className="w-full text-xs border-collapse min-w-[800px]">
          <thead>
            <tr className="bg-gray-100">
              <th className="border border-gray-200 px-2 py-1 text-left">Time</th>
              <th className="border border-gray-200 px-2 py-1 text-left">User</th>
              <th className="border border-gray-200 px-2 py-1 text-left">Action</th>
              <th className="border border-gray-200 px-2 py-1 text-left">Description</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 ? (
              <tr>
                <td colSpan={4} className="border border-gray-200 px-3 py-6 text-center text-gray-800">
                  No audit entries in this range
                </td>
              </tr>
            ) : (
              rows.map((log, i) => (
                <tr key={`${log.id}-${i}`} className={i % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                  <td className="border border-gray-200 px-2 py-1 whitespace-nowrap font-mono text-gray-800">
                    {log.timestamp ? format(new Date(log.timestamp), 'yyyy-MM-dd HH:mm:ss') : '—'}
                  </td>
                  <td className="border border-gray-200 px-2 py-1">
                    {userNameById.get(Number(log.user_id)) ?? String(log.user_id)}
                  </td>
                  <td className="border border-gray-200 px-2 py-1">{log.action}</td>
                  <td className="border border-gray-200 px-2 py-1 break-words max-w-xl">{log.description}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

interface CompanySettingsBrief {
  company_name?: string;
  address1?: string | null;
  address2?: string | null;
  city?: string | null;
  postcode?: string | null;
  country?: string | null;
  logo_url?: string | null;
  brand_color?: string | null;
  email_address?: string | null;
  phone?: string | null;
  website?: string | null;
  report_header?: string | null;
  email_footer?: string | null;
}

function yn(v: unknown): string {
  if (v === true || v === 'true' || v === 1 || v === '1') return 'Yes';
  if (v === false || v === 'false' || v === 0 || v === '0') return 'No';
  return '—';
}

/** Remote-alert payload may use `sensors` (data API) or `sensorIds` (legacy). */
function remoteGroupSensorIds(g: Record<string, unknown>): number[] {
  const raw = g.sensors ?? g.sensorIds;
  if (!Array.isArray(raw)) return [];
  return raw.map(x => Number(x)).filter(Number.isFinite);
}

function recipientUserId(row: Record<string, unknown>): string | number | null {
  return (row.user_id ?? row.userId ?? null) as string | number | null;
}

/** Threshold rows from `GET /alarms` — see AlarmsPage (snake_case, not AlarmData.settings). */
function parseAlarmConfigSensorId(row: Record<string, unknown>): number | null {
  const v = row.sensor_id ?? row.sensorId;
  if (typeof v === 'number' && Number.isFinite(v)) return v;
  if (typeof v === 'string' && /^\s*\d+\s*$/.test(v)) return parseInt(v.trim(), 10);
  return null;
}

interface ZoneSensorBrief {
  sensor_id: number;
  sensor_name: string;
  sensor_serialNo: number | string | null;
}

const SystemConfigReport: React.FC<{
  sensors: Sensor[];
  generatedAt: Date;
  brandName?: string;
  logoUrl?: string;
}> = ({ sensors, generatedAt, brandName, logoUrl }) => {
  const user = useSelector((state: RootState) => state.auth.user);
  const { data: zones = [], isLoading: zonesLoading } = useGetZonesQuery();
  const { data: alarms = [], isLoading: alarmsLoading } = useGetAlarmsQuery(undefined, { skip: !user });
  const { data: remoteGroups = [], isLoading: remLoading } = useGetRemoteAlertGroupsQuery();
  const { data: baseUnits = [], isLoading: buLoading } = useGetBaseUnitListQuery();
  const { data: baseUnitTypes = [], isLoading: buTypesLoading } = useGetBaseUnitTypesQuery();

  const [companySettings, setCompanySettings] = useState<CompanySettingsBrief>({});
  const [companyFetched, setCompanyFetched] = useState(false);
  const [zoneSensorsErr, setZoneSensorsErr] = useState<string | null>(null);
  const [zoneToSensors, setZoneToSensors] = useState<Record<number, ZoneSensorBrief[]>>({});

  useEffect(() => {
    let cancel = false;
    (async () => {
      try {
        const res = await fetch(`${API_CONFIG.baseURL}/company-settings`, {
          credentials: 'include',
          headers: prepareCommonHeaders(),
        });
        const data = res.ok ? await res.json().catch(() => ({})) : {};
        if (!cancel) setCompanySettings(data && typeof data === 'object' ? data : {});
      } catch {
        if (!cancel) setCompanySettings({});
      } finally {
        if (!cancel) setCompanyFetched(true);
      }
    })();
    return () => {
      cancel = true;
    };
  }, []);

  useEffect(() => {
    if (zonesLoading) return;
    const active = zones.filter(z => !z.is_deleted);

    let cancel = false;
    setZoneSensorsErr(null);
    setZoneToSensors({});

    (async () => {
      try {
        const pairs = await Promise.all(
          active.map(async z => {
            const res = await fetch(`${API_CONFIG.baseURL}/zones/${z.zone_id}/sensors`, {
              credentials: 'include',
              headers: prepareCommonHeaders(),
            });
            if (!res.ok) throw new Error(`Zone ${z.zone_id} sensors: ${res.status}`);
            const data: ZoneSensorBrief[] = await res.json();
            return [z.zone_id, Array.isArray(data) ? data : []] as const;
          }),
        );

        const map: Record<number, ZoneSensorBrief[]> = {};

        pairs.forEach(([zoneId, list]) => {
          map[zoneId] = list;
        });

        if (!cancel) setZoneToSensors(map);
      } catch (e) {
        if (!cancel)
          setZoneSensorsErr(e instanceof Error ? e.message : String(e));
      }
    })();

    return () => {
      cancel = true;
    };
  }, [zones, zonesLoading]);

  const loading =
    zonesLoading || alarmsLoading || remLoading || buLoading || buTypesLoading || !companyFetched;

  const activeZones = zones.filter(z => !z.is_deleted);

  const sensorsSorted = useMemo(
    () => [...sensors].sort((a, b) => a.id - b.id),
    [sensors],
  );

  const sensorNameById = useMemo(() => new Map(sensors.map(s => [s.id, s.name])), [sensors]);

  const baseUnitTypeLabel = useCallback(
    (typeNum: number | null) =>
      baseUnitTypes.find(bt => bt.type === typeNum)?.description ?? (typeNum != null ? String(typeNum) : '—'),
    [baseUnitTypes],
  );

  return (
    <div className="bg-white text-gray-900 p-8 font-sans">
      <ReportHeader
        title="System Configuration Summary"
        subtitle="Company profile, full sensor inventory, zones, alarm thresholds, remote alert routing, and base units."
        generatedAt={generatedAt}
        brandName={brandName}
        logoUrl={logoUrl}
      />

      {loading && (
        <div className="flex items-center gap-2 text-gray-800 mb-4">
          <Loader2 className="w-4 h-4 animate-spin" /> Loading configuration…
        </div>
      )}

      <section className="mb-8 break-inside-avoid">
        <h2 className="text-lg font-bold border-b pb-2 mb-3">Company & branding</h2>
        <table className="w-full text-sm border-collapse">
          <tbody className="align-top">
            {[
              ['Company name', companySettings.company_name || '—'],
              ['Address line 1', companySettings.address1?.trim() || '—'],
              ['Address line 2', companySettings.address2?.trim() || '—'],
              ['City', companySettings.city?.trim() || '—'],
              ['Postcode', companySettings.postcode?.trim() || '—'],
              ['Country', companySettings.country?.trim() || '—'],
              ['Report header', companySettings.report_header || '—'],
              ['Email footer', companySettings.email_footer?.trim() || '—'],
              ['Email', companySettings.email_address || '—'],
              ['Phone', companySettings.phone || '—'],
              ['Website', companySettings.website || '—'],
              ['Brand colour', companySettings.brand_color || '—'],
            ].map(([k, v]) => (
              <tr key={k as string}>
                <td className="border border-gray-200 px-3 py-1 w-44 font-medium bg-gray-100 align-top">{k}</td>
                <td className="border border-gray-200 px-3 py-1 text-gray-900 break-words align-top">{v}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </section>

      <section className="mb-8 break-inside-avoid">
        <h2 className="text-lg font-bold border-b pb-2 mb-3">Sensors ({sensorsSorted.length})</h2>
        <p className="text-xs text-gray-800 mb-2">
          All sensors configured for your company including deleted/OOS rows (for auditing).
        </p>
        <div className="overflow-x-auto">
          <table className="w-full text-xs border-collapse min-w-[900px]">
            <thead>
              <tr className="bg-gray-100">
                <th className="border border-gray-200 px-2 py-1 text-left whitespace-nowrap">ID</th>
                <th className="border border-gray-200 px-2 py-1 text-left">Name</th>
                <th className="border border-gray-200 px-2 py-1 text-left whitespace-nowrap">Serial</th>
                <th className="border border-gray-200 px-2 py-1 text-left whitespace-nowrap">Type</th>
                <th className="border border-gray-200 px-2 py-1 text-left whitespace-nowrap">Firmware</th>
                <th className="border border-gray-200 px-2 py-1 text-left whitespace-nowrap">Battery</th>
                <th className="border border-gray-200 px-2 py-1 text-left whitespace-nowrap">Signal</th>
                <th className="border border-gray-200 px-2 py-1 text-left whitespace-nowrap">SNR</th>
                <th className="border border-gray-200 px-2 py-1 text-left whitespace-nowrap">Deleted</th>
                <th className="border border-gray-200 px-2 py-1 text-left whitespace-nowrap">Out of svc.</th>
                <th className="border border-gray-200 px-2 py-1 text-left whitespace-nowrap">Updated</th>
              </tr>
            </thead>
            <tbody>
              {sensorsSorted.map((s, i) => (
                <tr key={s.id} className={i % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                  <td className="border border-gray-200 px-2 py-1 whitespace-nowrap">{s.id}</td>
                  <td className="border border-gray-200 px-2 py-1">{s.name}</td>
                  <td className="border border-gray-200 px-2 py-1 font-mono whitespace-nowrap">{s.serialNo}</td>
                  <td className="border border-gray-200 px-2 py-1">{s.type || '—'}</td>
                  <td className="border border-gray-200 px-2 py-1 font-mono whitespace-nowrap">{s.firmware_version ?? '—'}</td>
                  <td className="border border-gray-200 px-2 py-1 whitespace-nowrap">{s.Battery || '—'}</td>
                  <td className="border border-gray-200 px-2 py-1 font-mono whitespace-nowrap">{s.RSSI ?? '—'}</td>
                  <td className="border border-gray-200 px-2 py-1 font-mono whitespace-nowrap">{s.SNR ?? '—'}</td>
                  <td className="border border-gray-200 px-2 py-1 whitespace-nowrap">{s.is_deleted ? 'Yes' : 'No'}</td>
                  <td className="border border-gray-200 px-2 py-1 whitespace-nowrap">{s.is_out_of_service ? 'Yes' : 'No'}</td>
                  <td className="border border-gray-200 px-2 py-1 whitespace-nowrap text-[10px]">
                    {s.updated_at
                      ? formatDateTime(adjustTimestampForDisplay(s.updated_at))
                      : s.created_at
                        ? formatDateTime(adjustTimestampForDisplay(s.created_at))
                        : '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      <section className="mb-8 break-inside-avoid">
        <h2 className="text-lg font-bold border-b pb-2 mb-3">
          Zones ({activeZones.length})
        </h2>
        {zoneSensorsErr && (
          <p className="text-xs text-amber-700 mb-2">Zone sensor detail: {zoneSensorsErr}</p>
        )}
        <div className="space-y-4">
          {activeZones.map(z => (
            <div key={z.zone_id} className="border border-gray-200 rounded p-3">
              <p className="font-semibold text-sm text-gray-900">
                {z.zone_name} <span className="text-xs font-normal text-gray-800">(id {z.zone_id})</span>
              </p>
              {z.description?.trim() ? (
                <p className="text-xs text-gray-800 mb-2 mt-1">{z.description.trim()}</p>
              ) : null}
              <p className="text-xs text-gray-800 mb-2">
                Sensors: {z.sensor_count} · Listed: {(zoneToSensors[z.zone_id]?.length ?? 0)}
              </p>
              {(zoneToSensors[z.zone_id]?.length ?? 0) === 0 ? (
                <p className="text-xs text-gray-800">None or still loading.</p>
              ) : (
                <table className="w-full text-xs border-collapse">
                  <thead>
                    <tr className="bg-gray-100">
                      <th className="border px-2 py-1 text-left">Sensor ID</th>
                      <th className="border px-2 py-1 text-left">Name</th>
                      <th className="border px-2 py-1 text-left">Serial</th>
                    </tr>
                  </thead>
                  <tbody>
                    {zoneToSensors[z.zone_id]!.map((row, ri) => (
                      <tr key={row.sensor_id} className={ri % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                        <td className="border px-2 py-1">{row.sensor_id}</td>
                        <td className="border px-2 py-1">{row.sensor_name}</td>
                        <td className="border px-2 py-1 font-mono">{String(row.sensor_serialNo ?? '')}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          ))}
        </div>
      </section>

      <section className="mb-8 break-inside-avoid">
        <h2 className="text-lg font-bold border-b pb-2 mb-3">
          Threshold / alarm configurations ({Array.isArray(alarms) ? alarms.length : 0})
        </h2>
        <p className="text-xs text-gray-800 mb-2">
          Per-sensor thresholds from the alarm service (same fields as Thresholds screen).
        </p>
        <div className="overflow-x-auto">
          <table className="w-full text-xs border-collapse min-w-[1000px]">
            <thead>
              <tr className="bg-gray-100">
                <th className="border px-2 py-1 text-left whitespace-nowrap">Sensor ID</th>
                <th className="border px-2 py-1 text-left">Sensor name</th>
                <th className="border px-2 py-1 text-right whitespace-nowrap">Low (°C)</th>
                <th className="border px-2 py-1 text-right whitespace-nowrap">High (°C)</th>
                <th className="border px-2 py-1 text-left whitespace-nowrap">Delay</th>
                <th className="border px-2 py-1 text-left whitespace-nowrap">Low enabled</th>
                <th className="border px-2 py-1 text-left whitespace-nowrap">High enabled</th>
                <th className="border px-2 py-1 text-left whitespace-nowrap">Battery low</th>
                <th className="border px-2 py-1 text-left whitespace-nowrap">Batt. (V)</th>
                <th className="border px-2 py-1 text-left whitespace-nowrap">Miss. data</th>
                <th className="border px-2 py-1 text-left whitespace-nowrap">Gap (min)</th>
                <th className="border px-2 py-1 text-left whitespace-nowrap">Alarms off</th>
              </tr>
            </thead>
            <tbody>
              {!Array.isArray(alarms) || alarms.length === 0 ? (
                <tr>
                  <td colSpan={12} className="border px-3 py-4 text-center text-gray-800">
                    No threshold rows loaded{!user ? ' — sign in to fetch alarm configuration.' : ' — alarm service returned an empty list, or thresholds are still loading.'}
                  </td>
                </tr>
              ) : (
                (alarms as Record<string, unknown>[])
                  .slice(0, 500)
                  .map((row, i: number) => {
                    const sid = parseAlarmConfigSensorId(row);
                    const low =
                      row.low_alarm != null && String(row.low_alarm).trim() !== '' ? String(row.low_alarm) : '—';
                    const high =
                      row.high_alarm != null && String(row.high_alarm).trim() !== ''
                        ? String(row.high_alarm)
                        : '—';
                    const delay =
                      row.delay != null && String(row.delay).trim() !== '' ? String(row.delay) : '—';
                    const battV =
                      row.battery_low_voltage != null && String(row.battery_low_voltage).trim() !== ''
                        ? String(row.battery_low_voltage)
                        : '—';
                    const gapMin =
                      row.missing_data_minutes != null && String(row.missing_data_minutes).trim() !== ''
                        ? String(row.missing_data_minutes)
                        : '—';
                    return (
                      <tr key={`alarm-cfg-${sid ?? 'x'}-${i}`} className={i % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                        <td className="border px-2 py-1 whitespace-nowrap font-mono">{sid ?? '—'}</td>
                        <td className="border px-2 py-1">{sid != null ? sensorNameById.get(sid) ?? '—' : '—'}</td>
                        <td className="border px-2 py-1 text-right whitespace-nowrap">{low}</td>
                        <td className="border px-2 py-1 text-right whitespace-nowrap">{high}</td>
                        <td className="border px-2 py-1 whitespace-nowrap">{delay}</td>
                        <td className="border px-2 py-1 whitespace-nowrap">{yn(row.low_enable)}</td>
                        <td className="border px-2 py-1 whitespace-nowrap">{yn(row.high_enable)}</td>
                        <td className="border px-2 py-1 whitespace-nowrap">{yn(row.battery_low_enable)}</td>
                        <td className="border px-2 py-1 whitespace-nowrap font-mono">{battV}</td>
                        <td className="border px-2 py-1 whitespace-nowrap">{yn(row.missing_data_enable)}</td>
                        <td className="border px-2 py-1 whitespace-nowrap font-mono">{gapMin}</td>
                        <td className="border px-2 py-1 whitespace-nowrap">{yn(row.alarms_disabled)}</td>
                      </tr>
                    );
                  })
              )}
            </tbody>
          </table>
        </div>
        {Array.isArray(alarms) && alarms.length > 500 && (
          <p className="text-xs text-amber-700 mt-1">Showing first 500 alarm rows only for PDF size.</p>
        )}
      </section>

      <section className="mb-8 break-inside-avoid">
        <h2 className="text-lg font-bold border-b pb-2 mb-3">
          Remote alert groups ({Array.isArray(remoteGroups) ? remoteGroups.length : 0})
        </h2>
        {!Array.isArray(remoteGroups) || remoteGroups.length === 0 ? (
          <p className="text-sm text-gray-800">No remote alert groups configured.</p>
        ) : (
          <div className="space-y-6">
            {remoteGroups.map((raw, gi) => {
              const g = raw as Record<string, unknown>;
              const sidList = remoteGroupSensorIds(g);
              const recipients = Array.isArray(g.recipients) ? g.recipients as Record<string, unknown>[] : [];
              const grpId = g.id != null ? String(g.id) : String(gi);
              return (
                <div key={grpId} className="border border-gray-200 rounded p-4 break-inside-avoid">
                  <p className="font-semibold text-sm text-gray-900">
                    {String(g.name ?? '—')} <span className="font-normal text-gray-800 text-xs">(group id {grpId})</span>
                  </p>
                  <p className="text-xs text-gray-800 mt-1 mb-2">
                    Notify: temperature {yn(g.notifyTemperature)} · battery {yn(g.notifyBattery)} · missing data{' '}
                    {yn(g.notifyMissingData)}
                  </p>
                  <p className="text-xs font-semibold text-gray-900 mb-1">Assigned sensors ({sidList.length})</p>
                  {sidList.length === 0 ? (
                    <p className="text-xs text-gray-800 mb-3">None</p>
                  ) : (
                    <table className="w-full text-xs border-collapse mb-3">
                      <thead>
                        <tr className="bg-gray-100">
                          <th className="border px-2 py-1 text-left">Sensor ID</th>
                          <th className="border px-2 py-1 text-left">Name</th>
                        </tr>
                      </thead>
                      <tbody>
                        {sidList.map((sid, ri) => (
                          <tr key={`${grpId}-${sid}`} className={ri % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                            <td className="border px-2 py-1">{sid}</td>
                            <td className="border px-2 py-1">{sensorNameById.get(sid) ?? '—'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                  <p className="text-xs font-semibold text-gray-900 mb-1">Recipients ({recipients.length})</p>
                  {recipients.length === 0 ? (
                    <p className="text-xs text-gray-800">None</p>
                  ) : (
                    <table className="w-full text-xs border-collapse">
                      <thead>
                        <tr className="bg-gray-100">
                          <th className="border px-2 py-1 text-left">User ID</th>
                          <th className="border px-2 py-1 text-left">Email</th>
                          <th className="border px-2 py-1 text-left">SMS</th>
                          <th className="border px-2 py-1 text-left">Push</th>
                        </tr>
                      </thead>
                      <tbody>
                        {recipients.map((r, ri) => (
                          <tr
                            key={`${grpId}-${String(recipientUserId(r))}-${ri}`}
                            className={ri % 2 === 0 ? 'bg-white' : 'bg-gray-50'}
                          >
                            <td className="border px-2 py-1">{recipientUserId(r) ?? '—'}</td>
                            <td className="border px-2 py-1">{yn(r.email)}</td>
                            <td className="border px-2 py-1">{yn(r.sms)}</td>
                            <td className="border px-2 py-1">{yn(r.push)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </section>

      <section className="mb-8 break-inside-avoid">
        <h2 className="text-lg font-bold border-b pb-2 mb-3">
          Base units ({baseUnits?.length ?? 0})
        </h2>
        <div className="overflow-x-auto">
          <table className="w-full text-xs border-collapse min-w-[900px]">
            <thead>
              <tr className="bg-gray-100">
                <th className="border border-gray-200 px-2 py-1 text-left whitespace-nowrap">ID</th>
                <th className="border border-gray-200 px-2 py-1 text-left">Name</th>
                <th className="border border-gray-200 px-2 py-1 text-left whitespace-nowrap">Serial</th>
                <th className="border border-gray-200 px-2 py-1 text-left">Type</th>
                <th className="border border-gray-200 px-2 py-1 text-left">Description</th>
                <th className="border border-gray-200 px-2 py-1 text-left whitespace-nowrap">Deleted</th>
                <th className="border border-gray-200 px-2 py-1 text-left whitespace-nowrap">Last contact</th>
                <th className="border border-gray-200 px-2 py-1 text-left whitespace-nowrap">In contact</th>
                <th className="border border-gray-200 px-2 py-1 text-left whitespace-nowrap">Updated</th>
              </tr>
            </thead>
            <tbody>
              {(baseUnits ?? []).length === 0 ? (
                <tr>
                  <td colSpan={9} className="border border-gray-200 px-3 py-4 text-center text-gray-800">
                    No base units
                  </td>
                </tr>
              ) : (
                (baseUnits ?? []).map((bu, i) => (
                  <tr key={bu.id} className={i % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                    <td className="border border-gray-200 px-2 py-1 whitespace-nowrap">{bu.id}</td>
                    <td className="border border-gray-200 px-2 py-1">{bu.base_unit_name}</td>
                    <td className="border border-gray-200 px-2 py-1 font-mono whitespace-nowrap">{bu.serialNo}</td>
                    <td className="border border-gray-200 px-2 py-1 whitespace-nowrap">{baseUnitTypeLabel(bu.type ?? null)}</td>
                    <td className="border border-gray-200 px-2 py-1 break-words">{bu.description ?? '—'}</td>
                    <td className="border border-gray-200 px-2 py-1 whitespace-nowrap">{bu.is_deleted ? 'Yes' : 'No'}</td>
                    <td className="border border-gray-200 px-2 py-1 whitespace-nowrap tabular-nums">
                      {bu.last_contact ? formatDateTime(adjustTimestampForDisplay(bu.last_contact)) : '—'}
                    </td>
                    <td className="border border-gray-200 px-2 py-1 whitespace-nowrap">{bu.in_contact ? 'Yes' : 'No'}</td>
                    <td className="border border-gray-200 px-2 py-1 whitespace-nowrap tabular-nums">
                      {bu.updated_at ? formatDateTime(adjustTimestampForDisplay(bu.updated_at)) : '—'}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
};

// ─── Alarm report ─────────────────────────────────────────────────────────────

const AlarmReport: React.FC<{
  sensors: Sensor[];
  alarmLog: unknown[];
  alarmLoading: boolean;
  startDate: string;
  endDate: string;
  generatedAt: Date;
  onlyUnacknowledged: boolean;
  alarmFilterSensorIds: number[];
  brandName?: string;
  logoUrl?: string;
}> = ({
  sensors,
  alarmLog,
  alarmLoading,
  startDate,
  endDate,
  generatedAt,
  onlyUnacknowledged,
  alarmFilterSensorIds,
  brandName,
  logoUrl,
}) => {
  const events = useMemo(() => groupAlarmLogEvents(Array.isArray(alarmLog) ? alarmLog : []), [alarmLog]);

  const filtered = useMemo(
    () =>
      filterGroupedAlarms(events, {
        startDate,
        endDate,
        onlyUnacknowledged,
        sensorIds: alarmFilterSensorIds.length > 0 ? alarmFilterSensorIds : undefined,
      }),
    [events, startDate, endDate, onlyUnacknowledged, alarmFilterSensorIds],
  );

  const filterNote =
    alarmFilterSensorIds.length > 0
      ? ` · ${alarmFilterSensorIds.length} sensor(s) selected`
      : ' · All sensors';

  const totalEvents = filtered.length;
  const highCount   = filtered.filter(e => e.alarm_type === 'high').length;
  const lowCount    = filtered.filter(e => e.alarm_type === 'low').length;
  const unackCount  = filtered.filter(e => !e.acknowledged).length;

  const totalDurationMs = filtered.reduce((sum, e) => {
    if (e.activated && e.alarm_reset) {
      return sum + Math.max(0, new Date(e.alarm_reset).getTime() - new Date(e.activated).getTime());
    }
    return sum;
  }, 0);

  const sensorById = new Map(sensors.map(s => [s.id, s]));
  const sensorName = (id: number) => sensorById.get(id)?.name ?? `Sensor ${id}`;

  const sensorCounts = new Map<number, number>();
  filtered.forEach(e => sensorCounts.set(e.sensor_id, (sensorCounts.get(e.sensor_id) ?? 0) + 1));
  const topSensors = Array.from(sensorCounts.entries())
    .sort((a, b) => b[1] - a[1])
    .slice(0, 5);

  const subtitleSuffix = `${onlyUnacknowledged ? ' · Unacknowledged only' : ''}${filterNote}`;

  if (alarmLoading) {
    return (
      <div className="bg-white text-gray-900 p-8 font-sans">
        <ReportHeader title="Alarm Report" subtitle="Loading alarm log…" generatedAt={generatedAt} brandName={brandName} logoUrl={logoUrl} />
        <div className="flex items-center gap-2 text-gray-800">
          <Loader2 className="w-4 h-4 animate-spin" /> Loading…
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white text-gray-900 p-8 font-sans">
      <ReportHeader
        title="Alarm Report"
        subtitle={`${format(new Date(startDate), 'dd MMM yyyy HH:mm')} → ${format(new Date(endDate), 'dd MMM yyyy HH:mm')}${subtitleSuffix}`}
        generatedAt={generatedAt}
        brandName={brandName}
        logoUrl={logoUrl}
      />

      {/* Top stats */}
      <div className="grid grid-cols-4 gap-4 mb-6">
        <div className="border border-gray-200 rounded p-4 text-center">
          <p className="text-3xl font-bold text-gray-900">{totalEvents}</p>
          <p className="text-sm text-gray-800 mt-1">Total events</p>
        </div>
        <div className="border border-gray-200 rounded p-4 text-center">
          <p className="text-3xl font-bold text-red-600">{highCount}</p>
          <p className="text-sm text-gray-800 mt-1">High temp</p>
        </div>
        <div className="border border-gray-200 rounded p-4 text-center">
          <p className="text-3xl font-bold text-blue-600">{lowCount}</p>
          <p className="text-sm text-gray-800 mt-1">Low temp</p>
        </div>
        <div className="border border-gray-200 rounded p-4 text-center">
          <p className="text-3xl font-bold text-amber-600">{unackCount}</p>
          <p className="text-sm text-gray-800 mt-1">Unacknowledged</p>
        </div>
      </div>

      {/* Secondary stats */}
      <div className="grid grid-cols-2 gap-4 mb-6">
        <div className="border border-gray-200 rounded p-4">
          <p className="text-xs uppercase tracking-wide text-gray-800 font-medium mb-1">Total time in alarm</p>
          <p className="text-xl font-bold text-gray-900">{formatDurationLong(totalDurationMs)}</p>
          <p className="text-xs text-gray-800 mt-1">Sum of resolved event durations</p>
        </div>
        <div className="border border-gray-200 rounded p-4">
          <p className="text-xs uppercase tracking-wide text-gray-800 font-medium mb-2">Top sensors by alarm count</p>
          {topSensors.length === 0 ? (
            <p className="text-sm text-gray-800">—</p>
          ) : (
            <ul className="space-y-1 text-sm text-gray-900">
              {topSensors.map(([sId, count]) => (
                <li key={sId} className="flex justify-between gap-2">
                  <span className="truncate font-medium">{sensorName(sId)}</span>
                  <span className="font-mono text-gray-900 ml-2 flex-shrink-0 tabular-nums">{count}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      {/* Event table */}
      <table className="w-full text-sm border-collapse">
        <thead>
          <tr className="bg-gray-100">
            <th className="border border-gray-200 px-3 py-2 text-left text-gray-900 font-semibold">Sensor</th>
            <th className="border border-gray-200 px-3 py-2 text-left text-gray-900 font-semibold">Type</th>
            <th className="border border-gray-200 px-3 py-2 text-right text-gray-900 font-semibold">Worst (°C)</th>
            <th className="border border-gray-200 px-3 py-2 text-left text-gray-900 font-semibold">Started</th>
            <th className="border border-gray-200 px-3 py-2 text-left text-gray-900 font-semibold">Ended</th>
            <th className="border border-gray-200 px-3 py-2 text-left text-gray-900 font-semibold">Duration</th>
            <th className="border border-gray-200 px-3 py-2 text-left text-gray-900 font-semibold">Ack'd by</th>
            <th className="border border-gray-200 px-3 py-2 text-left text-gray-900 font-semibold min-w-[220px]">Notes</th>
          </tr>
        </thead>
        <tbody>
          {filtered.map((e, i) => (
            <tr key={e.event_id || `${e.idAlarmLog}-${i}`} className={i % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
              <td className="border border-gray-200 px-3 py-2 font-medium">{sensorName(e.sensor_id)}</td>
              <td className="border border-gray-200 px-3 py-2">
                <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${
                  e.alarm_type === 'high' ? 'bg-red-100 text-red-800' : 'bg-blue-100 text-blue-800'
                }`}>
                  {e.alarm_type === 'high' ? '▲ High' : '▼ Low'}
                </span>
              </td>
              <td className="border border-gray-200 px-3 py-2 text-right font-mono">
                {typeof e.alarm_values === 'number' ? e.alarm_values.toFixed(1) : '—'}
              </td>
              <td className="border border-gray-200 px-3 py-2 text-xs text-gray-900 tabular-nums">
                {e.activated ? format(new Date(e.activated), 'dd MMM HH:mm') : '—'}
              </td>
              <td className="border border-gray-200 px-3 py-2 text-xs text-gray-900 tabular-nums">
                {e.alarm_reset ? format(new Date(e.alarm_reset), 'dd MMM HH:mm') : <span className="italic text-amber-700">ongoing</span>}
              </td>
              <td className="border border-gray-200 px-3 py-2 text-xs text-gray-900">
                {e.activated && e.alarm_reset
                  ? formatDurationLong(new Date(e.alarm_reset).getTime() - new Date(e.activated).getTime())
                  : '—'}
              </td>
              <td className="border border-gray-200 px-3 py-2 text-xs text-gray-900">
                {e.acknowledged
                  ? (e.acknowledged_by || '—')
                  : <span className="text-amber-700 font-semibold">Pending</span>}
              </td>
              <td className="border border-gray-200 px-3 py-2 text-xs text-gray-900 min-w-[220px]">{e.notes || '—'}</td>
            </tr>
          ))}
          {filtered.length === 0 && (
            <tr>
              <td colSpan={8} className="border border-gray-200 px-3 py-6 text-center text-gray-800">
                No alarm events in this period
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
};

async function fetchSensorReadingsForExport(
  sensorId: number,
  startIso: string,
  endIso: string,
  bucketMinutes?: number,
): Promise<SensorReading[]> {
  const u = new URL(`${API_CONFIG.baseURL}/sensors/${sensorId}/readings`);
  u.searchParams.set('startDate', startIso);
  u.searchParams.set('endDate', endIso);
  if (bucketMinutes != null) u.searchParams.set('bucketMinutes', String(bucketMinutes));
  const res = await fetch(u.toString(), { credentials: 'include', headers: prepareCommonHeaders() });
  if (!res.ok) throw new Error(`Sensor ${sensorId} readings: ${res.status}`);
  const data = await res.json();
  if (!Array.isArray(data)) return [];
  return data.map((reading: any): SensorReading => {
    const rawTemp = reading.temperature;
    const temp = rawTemp != null ? parseFloat(rawTemp) : null;
    const parsedTemp =
      temp === null || temp === 8001 || temp === 8002 || (temp !== null && Number.isNaN(temp)) ? null : temp;
    const rawMin = reading.temp_min;
    const rawMax = reading.temp_max;
    const tm = rawMin != null && rawMin !== '' ? parseFloat(rawMin) : null;
    const tx = rawMax != null && rawMax !== '' ? parseFloat(rawMax) : null;
    const temp_min = tm !== null && Number.isFinite(tm) ? tm : null;
    const temp_max = tx !== null && Number.isFinite(tx) ? tx : null;
    return {
      timestamp: reading.timestamp,
      temperature: parsedTemp,
      temp_min,
      temp_max,
      temperature_2:
        reading.temperature_2 !== undefined && reading.temperature_2 !== null
          ? parseFloat(reading.temperature_2)
          : undefined,
      hum: reading.hum != null ? parseFloat(reading.hum) : undefined,
      battery: parseFloat(reading.Battery || reading.battery || '0'),
      rssi:
        reading.RSSI !== null && reading.RSSI !== undefined && reading.RSSI !== ''
          ? parseInt(String(reading.RSSI), 10)
          : null,
      snr:
        reading.SNR !== null && reading.SNR !== undefined && reading.SNR !== ''
          ? parseInt(String(reading.SNR), 10)
          : null,
      sample_count:
        reading.sample_count != null && reading.sample_count !== ''
          ? parseInt(String(reading.sample_count), 10) || 1
          : 1,
    };
  });
}

// ─── Main page ────────────────────────────────────────────────────────────────

const AnalyticsPage: React.FC = () => {
  const branding = useSelector((state: RootState) => state.branding);
  const user = useSelector((state: RootState) => state.auth.user);
  const brandName = branding.reportHeader || branding.companyName || undefined;
  const logoUrl = branding.logoUrl || undefined;
  const { isAdmin } = usePermissions();

  // Report config state
  const [reportType, setReportType] = useState<ReportType>('summary');
  const [selectedSensorIds, setSelectedSensorIds] = useState<number[]>([]);
  const [alarmFilterSensorIds, setAlarmFilterSensorIds] = useState<number[]>([]);
  const [preset, setPreset] = useState<number>(168); // 7 days default
  const [customStart, setCustomStart] = useState('');
  const [customEnd, setCustomEnd] = useState('');
  const [useCustom, setUseCustom] = useState(false);
  const [generated, setGenerated] = useState(false);
  const [generatedAt, setGeneratedAt] = useState<Date>(new Date());
  const [exporting, setExporting] = useState(false);
  const [csvExporting, setCsvExporting] = useState(false);
  const [historyDropdownOpen, setHistoryDropdownOpen] = useState(false);
  const [alarmDropdownOpen, setAlarmDropdownOpen] = useState(false);
  const [bucketMinutes, setBucketMinutes] = useState(60);
  const [historyChartsPdfOrientation, setHistoryChartsPdfOrientation] = useState<'portrait' | 'landscape'>('landscape');
  const [alarmsOnlyUnack, setAlarmsOnlyUnack] = useState(false);
  const [emailDialogOpen, setEmailDialogOpen] = useState(false);
  const [emailSending, setEmailSending] = useState(false);
  const [emailSelectedUserIds, setEmailSelectedUserIds] = useState<number[]>([]);
  const [emailExternalInput, setEmailExternalInput] = useState('');
  const [emailMessage, setEmailMessage] = useState('');

  const previewRef = useRef<HTMLDivElement>(null);

  // Data fetches
  const { data: sensors = [] } = useGetSensorListQuery();
  const { data: allUsers = [] } = useGetUsersQuery();
  const { data: liveData = [] } = useGetLiveDataQuery({ timeWindow: 90 }, { skip: !user });
  const { data: zonesForCsv = [] } = useGetZonesQuery();
  const { data: alarmLogRaw = [], isLoading: alarmLogLoading } = useGetAlarmLogQuery({
    includeAcknowledged: true,
  });

  const alarmLogData = useMemo(
    () => filterAlarmLogsBySensorAccess(alarmLogRaw, sensors),
    [alarmLogRaw, sensors],
  );
  const activeSensors = sensors.filter(s => !s.is_deleted);

  // Date range for history report.
  // Add 2h buffer to end so BST sensor readings (stored 1h ahead of UTC) are not excluded.
  const now = new Date();
  const resolvedEnd = useCustom && customEnd
    ? adjustDateForAPI(new Date(customEnd)).toISOString()
    : new Date(now.getTime() + 2 * 60 * 60 * 1000).toISOString();
  const resolvedStart = useCustom && customStart
    ? adjustDateForAPI(new Date(customStart)).toISOString()
    : subHours(now, preset).toISOString();

  const selectedSensors = activeSensors.filter(s => selectedSensorIds.includes(s.id));

  const historyBucketEstimate = useMemo(
    () =>
      reportType === 'history' || reportType === 'history_charts'
        ? estimateBucketCountMs(resolvedStart, resolvedEnd, bucketMinutes)
        : 0,
    [reportType, resolvedStart, resolvedEnd, bucketMinutes],
  );

  const toggleSensor = (id: number) => {
    setSelectedSensorIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };

  const selectAll = () => setSelectedSensorIds(activeSensors.map(s => s.id));
  const clearAll = () => setSelectedSensorIds([]);

  const toggleAlarmSensorFilter = (id: number) => {
    setAlarmFilterSensorIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };

  const selectAllAlarmFilters = () => setAlarmFilterSensorIds(activeSensors.map(s => s.id));
  const clearAllAlarmFilters = () => setAlarmFilterSensorIds([]);

  const handleGenerate = () => {
    if (
      (reportType === 'history' || reportType === 'history_charts' || reportType === 'minmax') &&
      selectedSensorIds.length === 0
    )
      return;
    if ((reportType === 'audit' || reportType === 'config') && !isAdmin) return;
    setGeneratedAt(new Date());
    setGenerated(true);
  };

  const handleExportPdf = useCallback(async () => {
    if (!previewRef.current) return;
    setExporting(true);
    const el = previewRef.current;
    // Remove height/overflow constraints so html2canvas captures the full content
    // Force a fixed desktop width so mobile layout doesn't get captured at phone screen width
    el.style.maxHeight = 'none';
    el.style.overflow = 'visible';
    el.style.width = '1400px';
    el.style.minWidth = '1400px';
    await waitForLayout();
    try {
      const typeLabel = REPORT_TYPE_LABELS[reportType] ?? 'Report';
      const prefix = (branding.companyName || 'Report').replace(/\s+/g, '-');
      const slugDate = format(generatedAt, 'yyyy-MM-dd');
      const fname =
        reportType === 'history_charts'
          ? `${prefix}-${typeLabel}-${slugDate}-${historyChartsPdfOrientation === 'landscape' ? 'Landscape' : 'Portrait'}.pdf`
          : `${prefix}-${typeLabel}-${slugDate}.pdf`;
      const brandOpts = {
        companyName:  branding.companyName  || undefined,
        logoUrl:      branding.logoUrl      || undefined,
        phone:        branding.phone        || undefined,
        website:      branding.website      || undefined,
        emailAddress: branding.emailAddress || undefined,
      };

      if (reportType === 'history') {
        await exportHistoryReportPdf(el, fname, brandOpts, generatedAt);
      } else if (reportType === 'history_charts') {
        await exportSensorChartPagesPdf(el, fname, brandOpts, generatedAt, historyChartsPdfOrientation);
      } else {
        await exportToPDF(el, fname, brandOpts, generatedAt);
      }
    } finally {
      el.style.maxHeight = '';
      el.style.overflow = '';
      el.style.width = '';
      el.style.minWidth = '';
      setExporting(false);
    }
  }, [reportType, generatedAt, branding, historyChartsPdfOrientation]);

  const toggleEmailUser = (id: number) =>
    setEmailSelectedUserIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);

  const handleSendReportEmail = useCallback(async () => {
    if (!previewRef.current) return;

    const userEmails = allUsers.filter(u => emailSelectedUserIds.includes(u.id)).map(u => u.email);
    const externalEmails = emailExternalInput.split(/[,;\s]+/).map(s => s.trim()).filter(Boolean);
    const recipients = Array.from(new Set([...userEmails, ...externalEmails]));
    if (recipients.length === 0) {
      window.alert('Add at least one recipient');
      return;
    }

    setEmailSending(true);
    const el = previewRef.current;
    el.style.maxHeight = 'none';
    el.style.overflow = 'visible';
    el.style.width = '1400px';
    el.style.minWidth = '1400px';
    await waitForLayout();
    try {
      const typeLabel = REPORT_TYPE_LABELS[reportType] ?? 'Report';
      const prefix = (branding.companyName || 'Report').replace(/\s+/g, '-');
      const slugDate = format(generatedAt, 'yyyy-MM-dd');
      const fname = `${prefix}-${typeLabel}-${slugDate}.pdf`;
      const brandOpts = {
        companyName:  branding.companyName  || undefined,
        logoUrl:      branding.logoUrl      || undefined,
        phone:        branding.phone        || undefined,
        website:      branding.website      || undefined,
        emailAddress: branding.emailAddress || undefined,
      };

      const pdf = reportType === 'history'
        ? await exportHistoryReportPdf(el, fname, brandOpts, generatedAt, { download: false })
        : reportType === 'history_charts'
          ? await exportSensorChartPagesPdf(el, fname, brandOpts, generatedAt, historyChartsPdfOrientation, { download: false })
          : await exportToPDF(el, fname, brandOpts, generatedAt, { download: false });

      const dataUri = pdf.output('datauristring');
      const base64 = dataUri.slice(dataUri.indexOf('base64,') + 7);

      const res = await fetch(`${API_CONFIG.baseURL}/reports/email`, {
        method: 'POST',
        credentials: 'include',
        headers: prepareCommonHeaders(),
        body: JSON.stringify({
          to: recipients,
          subject: `${typeLabel.replace(/-/g, ' ')} — ${prefix}`,
          message: emailMessage,
          filename: fname,
          attachmentBase64: base64,
        }),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.message || `Email send failed (${res.status})`);
      }

      setEmailDialogOpen(false);
      setEmailSelectedUserIds([]);
      setEmailExternalInput('');
      setEmailMessage('');
      window.alert('Report emailed successfully');
    } catch (err) {
      window.alert(err instanceof Error ? err.message : 'Failed to send report email');
    } finally {
      el.style.maxHeight = '';
      el.style.overflow = '';
      el.style.width = '';
      el.style.minWidth = '';
      setEmailSending(false);
    }
  }, [reportType, generatedAt, branding, historyChartsPdfOrientation, allUsers, emailSelectedUserIds, emailExternalInput, emailMessage]);

  const csvSupported = useMemo(
    () =>
      ['summary', 'history', 'history_charts', 'alarms', 'minmax', 'audit', 'config'].includes(reportType),
    [reportType],
  );

  const handleExportCsv = useCallback(async () => {
    const prefix = (branding.companyName || 'Report').replace(/\s+/g, '-');
    const slugDate = format(generatedAt, 'yyyy-MM-dd');

    setCsvExporting(true);
    try {
      switch (reportType) {
        case 'summary': {
          const liveMap = new Map(liveData.map(r => [r.sensor_id, r]));
          downloadCsv(`${prefix}-Sensor-Summary-${slugDate}.csv`, ['Name', 'Serial', 'Status', 'Temp_C', 'Battery', 'RSSI', 'LastSeen'], activeSensors.map(s => {
            const reading = liveMap.get(String(s.serialNo));
            const status = sensorStatus(reading);
            return [
              s.name,
              String(s.serialNo),
              status,
              reading?.temperature != null ? reading.temperature.toFixed(2) : '',
              reading?.Battery ?? '',
              reading?.RSSI ?? '',
              reading?.timestamp ? formatDateTime(adjustTimestampForDisplay(reading.timestamp)) : '',
            ];
          }));
          break;
        }
        case 'history':
        case 'history_charts': {
          if (
            historyBucketEstimate > 5000 &&
            !window.confirm(
              `This will export roughly ${historyBucketEstimate.toLocaleString()} rows per sensor (${selectedSensors.length} sensor(s)) at this granularity. It may take a while and produce a large file. Continue?`
            )
          ) {
            break;
          }
          const rowsOut: (string | number | null | undefined)[][] = [];
          for (const sensor of selectedSensors) {
            const raw = await fetchSensorReadingsForExport(sensor.id, resolvedStart, resolvedEnd, bucketMinutes);
            const valid = raw
              .filter(r => r.temperature !== null)
              .map(r => ({ timestamp: r.timestamp, temperature: r.temperature as number }));
            const buckets = aggregateReadingsIntoBuckets(valid, bucketMinutes);
            buckets.forEach(b => rowsOut.push([sensor.name, String(sensor.serialNo), b.label, b.avg]));
          }
          downloadCsv(`${prefix}-Temperature-${reportType === 'history_charts' ? 'Charts' : 'History'}-${slugDate}.csv`, ['Sensor', 'Serial', 'Period', 'AvgTemp_C'], rowsOut);
          break;
        }
        case 'alarms': {
          const grouped = filterGroupedAlarms(groupAlarmLogEvents(Array.isArray(alarmLogData) ? alarmLogData : []), {
            startDate: resolvedStart,
            endDate: resolvedEnd,
            onlyUnacknowledged: alarmsOnlyUnack,
            sensorIds: alarmFilterSensorIds.length > 0 ? alarmFilterSensorIds : undefined,
          });
          const nameById = new Map(activeSensors.map(s => [s.id, s.name]));
          downloadCsv(`${prefix}-Alarm-Report-${slugDate}.csv`, ['Sensor', 'Type', 'Worst_C', 'Started', 'Ended', 'Duration', 'AckBy', 'Notes'], grouped.map(e => [
            nameById.get(e.sensor_id) ?? `Sensor ${e.sensor_id}`,
            e.alarm_type === 'high' ? 'High' : 'Low',
            typeof e.alarm_values === 'number' ? e.alarm_values.toFixed(1) : '',
            e.activated ? format(new Date(e.activated), 'yyyy-MM-dd HH:mm') : '',
            e.alarm_reset ? format(new Date(e.alarm_reset), 'yyyy-MM-dd HH:mm') : '',
            e.activated && e.alarm_reset
              ? formatDurationLong(new Date(e.alarm_reset).getTime() - new Date(e.activated).getTime())
              : '',
            e.acknowledged ? (e.acknowledged_by || '') : 'Pending',
            e.notes || '',
          ]));
          break;
        }
        case 'minmax': {
          const rowsMx: (string | number | null | undefined)[][] = [];
          for (const sensor of selectedSensors) {
            const raw = await fetchSensorReadingsForExport(sensor.id, resolvedStart, resolvedEnd);
            const { minV, minTs, maxV, maxTs, avg } = computeTemperatureMinMaxAvg(raw);
            rowsMx.push([
              sensor.name,
              String(sensor.serialNo),
              minV !== null ? minV.toFixed(2) : '',
              minTs ? format(adjustTimestampForDisplay(minTs), 'yyyy-MM-dd HH:mm') : '',
              maxV !== null ? maxV.toFixed(2) : '',
              maxTs ? format(adjustTimestampForDisplay(maxTs), 'yyyy-MM-dd HH:mm') : '',
              avg !== null ? avg.toFixed(2) : '',
            ]);
          }
          downloadCsv(`${prefix}-MinMax-${slugDate}.csv`, ['Sensor', 'Serial', 'Min_C', 'MinAt', 'Max_C', 'MaxAt', 'Avg_C'], rowsMx);
          break;
        }
        case 'audit': {
          const rowsAudit = await fetchAllAuditLogsForReport(resolvedStart, resolvedEnd);
          downloadCsv(`${prefix}-Audit-Log-${slugDate}.csv`, ['Timestamp', 'User ID', 'Action', 'Description'], rowsAudit.map(r => [
            r.timestamp ? format(new Date(r.timestamp), 'yyyy-MM-dd HH:mm:ss') : '',
            r.user_id,
            r.action,
            r.description ?? '',
          ]));
          break;
        }
        case 'config': {
          downloadCsv(`${prefix}-Sensors-${slugDate}.csv`, ['ID', 'Name', 'Serial', 'Deleted'], activeSensors.map(s => [
            s.id,
            s.name,
            String(s.serialNo),
            s.is_deleted ? 'Yes' : 'No',
          ]));
          const zrows: (string | number)[][] = (zonesForCsv.filter(z => !z.is_deleted) ?? []).map(z => [
            z.zone_id,
            z.zone_name,
            z.sensor_count,
          ]);
          downloadCsv(`${prefix}-Zones-${slugDate}.csv`, ['Zone_ID', 'Zone_Name', 'Sensor_Count'], zrows as (string | number | null | undefined)[][]);
          break;
        }
        default:
          break;
      }
    } catch (err) {
      console.error(err);
      window.alert(err instanceof Error ? err.message : 'CSV export failed');
    } finally {
      setCsvExporting(false);
    }
  }, [
    reportType,
    branding.companyName,
    generatedAt,
    liveData,
    activeSensors,
    selectedSensors,
    resolvedStart,
    resolvedEnd,
    bucketMinutes,
    historyBucketEstimate,
    alarmLogData,
    alarmsOnlyUnack,
    alarmFilterSensorIds,
    zonesForCsv,
  ]);

  return (
    <div className="min-h-screen bg-app-canvas dark:bg-gray-900">
      <div className="max-w-screen-xl mx-auto p-4 md:p-6">
        {/* Page title */}
        <div className="flex items-center gap-3 mb-4 md:mb-6">
          <div className="p-2 bg-blue-600 rounded-lg">
            <FileText className="w-5 h-5 text-white" />
          </div>
          <div>
            <h1 className="text-xl md:text-2xl font-bold text-gray-900 dark:text-white">Reports</h1>
            <p className="text-sm text-gray-500 dark:text-gray-400">Generate PDF and CSV exports</p>
          </div>
        </div>

        <div className="flex flex-col md:flex-row gap-4 md:gap-6 items-start">
          {/* ── Left panel: builder ── */}
          <div className="w-full md:w-72 md:flex-shrink-0 space-y-4">

            {/* Report type */}
            <div className="bg-app-panel dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-4">
              <h2 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3 flex items-center gap-2">
                <BarChart3 className="w-4 h-4" /> Report Type
              </h2>
              {(
                [
                  { id: 'summary' as const, label: 'Sensor Status Summary', desc: 'Snapshot of all sensors with current readings', adminOnly: false },
                  { id: 'history' as const, label: 'Temperature History', desc: 'Charts & data for selected sensors over a date range', adminOnly: false },
                  { id: 'history_charts' as const, label: 'Temperature charts (PDF)', desc: 'One full-page graph per sensor; choose landscape or portrait PDF', adminOnly: false },
                  { id: 'minmax' as const, label: 'Min–Max summary', desc: 'Per-sensor minimum, maximum, and average temperatures', adminOnly: false },
                  { id: 'zone' as const, label: 'Zone Summary', desc: 'Sensors grouped by zone', adminOnly: false },
                  { id: 'alarms' as const, label: 'Alarm Report', desc: 'High/low temperature alarm events with acknowledgements', adminOnly: false },
                  { id: 'audit' as const, label: 'Activity / Audit log', desc: 'User actions within a date range (admin only)', adminOnly: true },
                  { id: 'config' as const, label: 'System configuration', desc: 'Company settings, zones, thresholds, alerts, base units (admin only)', adminOnly: true },
                ]
              ).map(opt => (
                <label
                  key={opt.id}
                  className={`flex items-start gap-3 p-3 rounded-lg mb-2 border transition-colors ${
                    opt.adminOnly && !isAdmin ? 'opacity-50 cursor-not-allowed border-transparent' :
                    reportType === opt.id ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20 cursor-pointer' : 'border-transparent hover:bg-app-canvas dark:hover:bg-gray-700 cursor-pointer'
                  }`}
                >
                  <input
                    type="radio"
                    name="reportType"
                    value={opt.id}
                    disabled={Boolean(opt.adminOnly && !isAdmin)}
                    checked={reportType === opt.id}
                    onChange={() => {
                      if (opt.adminOnly && !isAdmin) return;
                      setReportType(opt.id);
                      setGenerated(false);
                    }}
                    className="mt-0.5 accent-blue-600"
                  />
                  <div>
                    <p className="text-sm font-medium text-gray-900 dark:text-white">{opt.label}</p>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">{opt.desc}</p>
                    {opt.adminOnly && !isAdmin && (
                      <p className="text-[10px] text-amber-600 dark:text-amber-400 mt-1">Administrators only</p>
                    )}
                  </div>
                </label>
              ))}
            </div>

            {(reportType === 'history' || reportType === 'history_charts' || reportType === 'minmax') && (
              <div className="bg-app-panel dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-4">
                <h2 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3 flex items-center gap-2">
                  <Wifi className="w-4 h-4" /> Sensors
                </h2>
                <p className="text-[10px] text-gray-500 dark:text-gray-400 mb-2">
                  Select one or more sensors for this report.
                </p>
                <div className="flex gap-2 mb-2">
                  <button type="button" onClick={selectAll} className="text-xs text-blue-600 hover:underline">All</button>
                  <span className="text-gray-300 dark:text-gray-600">|</span>
                  <button type="button" onClick={clearAll} className="text-xs text-blue-600 hover:underline">None</button>
                </div>
                <button
                  type="button"
                  onClick={() => setHistoryDropdownOpen(o => !o)}
                  className="w-full flex items-center justify-between text-sm px-3 py-2 border border-gray-200 dark:border-gray-600 rounded-lg text-gray-700 dark:text-gray-300 hover:bg-app-canvas dark:hover:bg-gray-700"
                >
                  <span>{selectedSensorIds.length === 0 ? 'Select sensors…' : `${selectedSensorIds.length} selected`}</span>
                  {historyDropdownOpen ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                </button>
                {historyDropdownOpen && (
                  <div className="mt-1 max-h-48 overflow-y-auto border border-gray-200 dark:border-gray-600 rounded-lg bg-app-panel dark:bg-gray-800">
                    {activeSensors.map(s => (
                      <label key={s.id} className="flex items-center gap-2 px-3 py-2 hover:bg-app-canvas dark:hover:bg-gray-700 cursor-pointer">
                        <input type="checkbox" checked={selectedSensorIds.includes(s.id)} onChange={() => toggleSensor(s.id)} className="accent-blue-600" />
                        <span className="text-xs text-gray-800 dark:text-gray-200 truncate">{s.name}</span>
                      </label>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Time bucket granularity — history & chart-PDF reports */}
            {(reportType === 'history' || reportType === 'history_charts') && (
              <div className="bg-app-panel dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-4">
                <h2 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3 flex items-center gap-2">
                  <Thermometer className="w-4 h-4" /> Granularity
                </h2>
                <select
                  value={bucketMinutes}
                  onChange={e => { setBucketMinutes(Number(e.target.value)); setGenerated(false); }}
                  className="w-full text-xs px-2 py-2 border border-gray-200 dark:border-gray-600 rounded-lg bg-app-inset dark:bg-gray-700 text-gray-800 dark:text-gray-200"
                >
                  {BUCKET_PRESETS.map(p => (
                    <option key={p.minutes} value={p.minutes}>{p.label}</option>
                  ))}
                </select>
                {historyBucketEstimate > MAX_CHART_BUCKET_WARN && (
                  <p className="text-[10px] text-amber-600 dark:text-amber-400 mt-2 leading-snug">
                    High bucket count (~{historyBucketEstimate.toLocaleString()}). Consider a wider granularity for readability.
                  </p>
                )}
              </div>
            )}

            {/* PDF page orientation — chart pages report only */}
            {reportType === 'history_charts' && (
              <div className="bg-app-panel dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-4">
                <h2 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">PDF orientation</h2>
                <p className="text-[10px] text-gray-500 dark:text-gray-400 mb-3">
                  Landscape gives a wider timeline on each page.
                </p>
                <div className="space-y-2">
                  <label className="flex items-center gap-2 cursor-pointer text-sm text-gray-700 dark:text-gray-300">
                    <input
                      type="radio"
                      name="historyChartsPdfOrientation"
                      checked={historyChartsPdfOrientation === 'landscape'}
                      onChange={() => { setHistoryChartsPdfOrientation('landscape'); setGenerated(false); }}
                      className="accent-blue-600"
                    />
                    Landscape
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer text-sm text-gray-700 dark:text-gray-300">
                    <input
                      type="radio"
                      name="historyChartsPdfOrientation"
                      checked={historyChartsPdfOrientation === 'portrait'}
                      onChange={() => { setHistoryChartsPdfOrientation('portrait'); setGenerated(false); }}
                      className="accent-blue-600"
                    />
                    Portrait
                  </label>
                </div>
              </div>
            )}

            {/* Alarm-specific filters */}
            {reportType === 'alarms' && (
              <div className="bg-app-panel dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-4 space-y-3">
                <h2 className="text-sm font-semibold text-gray-700 dark:text-gray-300 flex items-center gap-2">
                  <Bell className="w-4 h-4" /> Filters
                </h2>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={alarmsOnlyUnack}
                    onChange={e => { setAlarmsOnlyUnack(e.target.checked); setGenerated(false); }}
                    className="accent-blue-600"
                  />
                  <span className="text-sm text-gray-700 dark:text-gray-300">Unacknowledged only</span>
                </label>
                <div>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">Sensors (optional)</p>
                  <p className="text-[10px] text-gray-500 mb-2">Leave none selected to include all sensors.</p>
                  <div className="flex gap-2 mb-2">
                    <button type="button" onClick={selectAllAlarmFilters} className="text-xs text-blue-600 hover:underline">All</button>
                    <span className="text-gray-300 dark:text-gray-600">|</span>
                    <button type="button" onClick={clearAllAlarmFilters} className="text-xs text-blue-600 hover:underline">None</button>
                  </div>
                  <button
                    type="button"
                    onClick={() => setAlarmDropdownOpen(o => !o)}
                    className="w-full flex items-center justify-between text-sm px-3 py-2 border border-gray-200 dark:border-gray-600 rounded-lg text-gray-700 dark:text-gray-300 hover:bg-app-canvas dark:hover:bg-gray-700"
                  >
                    <span>
                      {alarmFilterSensorIds.length === 0
                        ? 'All sensors'
                        : `${alarmFilterSensorIds.length} sensor(s)`}
                    </span>
                    {alarmDropdownOpen ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                  </button>
                  {alarmDropdownOpen && (
                    <div className="mt-1 max-h-48 overflow-y-auto border border-gray-200 dark:border-gray-600 rounded-lg bg-app-panel dark:bg-gray-800">
                      {activeSensors.map(s => (
                        <label key={`alarm-${s.id}`} className="flex items-center gap-2 px-3 py-2 hover:bg-app-canvas dark:hover:bg-gray-700 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={alarmFilterSensorIds.includes(s.id)}
                            onChange={() => toggleAlarmSensorFilter(s.id)}
                            className="accent-blue-600"
                          />
                          <span className="text-xs text-gray-800 dark:text-gray-200 truncate">{s.name}</span>
                        </label>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Date range — used by history and alarms */}
            {(reportType === 'history' || reportType === 'history_charts' || reportType === 'alarms' || reportType === 'minmax' || reportType === 'audit') && (
              <div className="bg-app-panel dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-4">
                <h2 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3 flex items-center gap-2">
                  <Calendar className="w-4 h-4" /> Date Range
                </h2>
                <div className="flex flex-wrap gap-2 mb-3">
                  {DATE_PRESETS.map(p => (
                    <button
                      key={p.hours}
                      onClick={() => { setPreset(p.hours); setUseCustom(false); setGenerated(false); }}
                      className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${!useCustom && preset === p.hours ? 'bg-blue-600 border-blue-600 text-white' : 'border-gray-200 dark:border-gray-600 text-gray-600 dark:text-gray-400 hover:bg-app-canvas dark:hover:bg-gray-700'}`}
                    >
                      {p.label}
                    </button>
                  ))}
                  <button
                    onClick={() => { setUseCustom(true); setGenerated(false); }}
                    className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${useCustom ? 'bg-blue-600 border-blue-600 text-white' : 'border-gray-200 dark:border-gray-600 text-gray-600 dark:text-gray-400 hover:bg-app-canvas dark:hover:bg-gray-700'}`}
                  >
                    Custom
                  </button>
                </div>
                {useCustom && (
                  <div className="space-y-2">
                    <div>
                      <label className="text-xs text-gray-500 dark:text-gray-400 block mb-1">From</label>
                      <input type="datetime-local" value={customStart} onChange={e => { setCustomStart(e.target.value); setGenerated(false); }}
                        className="w-full text-xs px-2 py-1.5 border border-gray-200 dark:border-gray-600 rounded bg-app-inset dark:bg-gray-700 text-gray-800 dark:text-gray-200" />
                    </div>
                    <div>
                      <label className="text-xs text-gray-500 dark:text-gray-400 block mb-1">To</label>
                      <input type="datetime-local" value={customEnd} onChange={e => { setCustomEnd(e.target.value); setGenerated(false); }}
                        className="w-full text-xs px-2 py-1.5 border border-gray-200 dark:border-gray-600 rounded bg-app-inset dark:bg-gray-700 text-gray-800 dark:text-gray-200" />
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Generate button */}
            <button
              type="button"
              onClick={handleGenerate}
              disabled={
                ((reportType === 'history' || reportType === 'history_charts' || reportType === 'minmax') &&
                  selectedSensorIds.length === 0) ||
                ((reportType === 'audit' || reportType === 'config') && !isAdmin)
              }
              className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed dark:disabled:bg-gray-600 text-white rounded-xl font-medium transition-colors"
            >
              <BarChart3 className="w-4 h-4" />
              Generate Report
            </button>

            {(reportType === 'history' || reportType === 'history_charts' || reportType === 'minmax') && selectedSensorIds.length === 0 && (
              <p className="text-xs text-gray-400 text-center">Select at least one sensor</p>
            )}
          </div>

          {/* ── Right panel: preview ── */}
          <div className="w-full md:flex-1 min-w-0">
            {!generated ? (
              <div className="bg-app-panel dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 flex flex-col items-center justify-center py-24 text-center">
                <FileText className="w-12 h-12 text-gray-300 dark:text-gray-600 mb-4" />
                <p className="text-gray-500 dark:text-gray-400 font-medium">Configure your report and click Generate</p>
                <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">A preview will appear here</p>
              </div>
            ) : (
              <>
                {/* Export toolbar */}
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-3">
                  <p className="text-sm text-gray-500 dark:text-gray-400 flex items-center gap-1">
                    <Clock className="w-4 h-4" /> Generated {format(generatedAt, 'HH:mm:ss')}
                  </p>
                <div className="flex flex-wrap gap-2">
                  <button
                    type="button"
                    onClick={handleExportCsv}
                    disabled={csvExporting || !csvSupported}
                    title={csvSupported ? 'Download CSV data' : 'CSV not offered for this report type'}
                    className="flex items-center gap-2 px-4 py-2 bg-slate-600 hover:bg-slate-700 disabled:bg-gray-400 dark:disabled:bg-gray-600 text-white rounded-lg text-sm font-medium transition-colors"
                  >
                    {csvExporting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Table2 className="w-4 h-4" />}
                    {csvExporting ? 'Exporting CSV…' : 'Export CSV'}
                  </button>
                  <button
                    type="button"
                    onClick={handleExportPdf}
                    disabled={exporting}
                    className="flex items-center gap-2 px-4 py-2 bg-green-600 hover:bg-green-700 disabled:bg-gray-400 text-white rounded-lg text-sm font-medium transition-colors"
                  >
                    {exporting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
                    {exporting ? 'Exporting…' : 'Export PDF'}
                  </button>
                  <button
                    type="button"
                    onClick={() => setEmailDialogOpen(true)}
                    disabled={emailSending}
                    className="flex items-center gap-2 px-4 py-2 bg-indigo-600 hover:bg-indigo-700 disabled:bg-gray-400 text-white rounded-lg text-sm font-medium transition-colors"
                  >
                    <Mail className="w-4 h-4" />
                    Email Report
                  </button>
                </div>
                </div>

                {/* Preview area */}
                <div className="border-2 border-gray-200 dark:border-gray-600 rounded-xl overflow-hidden shadow-lg">
                  <div className="bg-app-canvas dark:bg-gray-700 px-4 py-2 text-xs text-gray-500 dark:text-gray-400 flex items-center gap-2">
                    <div className="w-2 h-2 rounded-full bg-red-400" />
                    <div className="w-2 h-2 rounded-full bg-yellow-400" />
                    <div className="w-2 h-2 rounded-full bg-green-400" />
                    <span className="ml-2">Report Preview</span>
                  </div>
                  <div ref={previewRef} className="overflow-auto max-h-[70vh] md:max-h-[calc(100vh-220px)]">
                    {reportType === 'summary' && (
                      <SummaryReport sensors={activeSensors} liveData={liveData} generatedAt={generatedAt} brandName={brandName} logoUrl={logoUrl} />
                    )}
                    {reportType === 'history_charts' && (
                      <HistoryChartsPdfReport
                        sensors={selectedSensors}
                        startDate={resolvedStart}
                        endDate={resolvedEnd}
                        bucketMinutes={bucketMinutes}
                        pdfOrientation={historyChartsPdfOrientation}
                      />
                    )}
                    {reportType === 'history' && (
                      <HistoryReport
                        sensors={selectedSensors}
                        startDate={resolvedStart}
                        endDate={resolvedEnd}
                        generatedAt={generatedAt}
                        bucketMinutes={bucketMinutes}
                        brandName={brandName}
                        logoUrl={logoUrl}
                      />
                    )}
                    {reportType === 'minmax' && (
                      <MinMaxReport
                        sensors={selectedSensors}
                        startDate={resolvedStart}
                        endDate={resolvedEnd}
                        generatedAt={generatedAt}
                        brandName={brandName}
                        logoUrl={logoUrl}
                      />
                    )}
                    {reportType === 'audit' && isAdmin && (
                      <AuditLogsReport
                        startDate={resolvedStart}
                        endDate={resolvedEnd}
                        generatedAt={generatedAt}
                        brandName={brandName}
                        logoUrl={logoUrl}
                      />
                    )}
                    {reportType === 'config' && isAdmin && (
                      <SystemConfigReport sensors={sensors} generatedAt={generatedAt} brandName={brandName} logoUrl={logoUrl} />
                    )}
                    {reportType === 'zone' && (
                      <ZoneReport sensors={activeSensors} liveData={liveData} generatedAt={generatedAt} brandName={brandName} logoUrl={logoUrl} />
                    )}
                    {reportType === 'alarms' && (
                      <AlarmReport
                        sensors={activeSensors}
                        alarmLog={alarmLogData}
                        alarmLoading={alarmLogLoading}
                        startDate={resolvedStart}
                        endDate={resolvedEnd}
                        generatedAt={generatedAt}
                        onlyUnacknowledged={alarmsOnlyUnack}
                        alarmFilterSensorIds={alarmFilterSensorIds}
                        brandName={brandName}
                        logoUrl={logoUrl}
                      />
                    )}
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      <Dialog
        isOpen={emailDialogOpen}
        onClose={() => !emailSending && setEmailDialogOpen(false)}
        panelClassName="max-w-lg"
      >
        <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4 text-left">Email Report</h3>
        <div className="space-y-4 text-left">
          <div>
            <label className="text-xs font-medium text-gray-500 dark:text-gray-400 block mb-1">TS1 users</label>
            <div className="max-h-40 overflow-y-auto border border-gray-200 dark:border-gray-600 rounded-lg">
              {allUsers.map(u => (
                <label key={u.id} className="flex items-center gap-2 px-3 py-2 hover:bg-app-canvas dark:hover:bg-gray-700 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={emailSelectedUserIds.includes(u.id)}
                    onChange={() => toggleEmailUser(u.id)}
                    className="accent-blue-600"
                  />
                  <span className="text-xs text-gray-800 dark:text-gray-200 truncate">
                    {[u.firstName, u.lastName].filter(Boolean).join(' ') || u.email} ({u.email})
                  </span>
                </label>
              ))}
              {allUsers.length === 0 && (
                <p className="text-xs text-gray-400 px-3 py-2">No users found</p>
              )}
            </div>
          </div>

          <div>
            <label className="text-xs font-medium text-gray-500 dark:text-gray-400 block mb-1">Other email addresses</label>
            <input
              type="text"
              value={emailExternalInput}
              onChange={e => setEmailExternalInput(e.target.value)}
              placeholder="client@example.com, another@example.com"
              className="w-full text-sm px-3 py-2 border border-gray-200 dark:border-gray-600 rounded-lg bg-app-inset dark:bg-gray-700 text-gray-800 dark:text-gray-200"
            />
          </div>

          <div>
            <label className="text-xs font-medium text-gray-500 dark:text-gray-400 block mb-1">Message (optional)</label>
            <textarea
              value={emailMessage}
              onChange={e => setEmailMessage(e.target.value)}
              rows={3}
              placeholder="Add a note for the recipient…"
              className="w-full text-sm px-3 py-2 border border-gray-200 dark:border-gray-600 rounded-lg bg-app-inset dark:bg-gray-700 text-gray-800 dark:text-gray-200"
            />
          </div>

          <button
            type="button"
            onClick={handleSendReportEmail}
            disabled={emailSending}
            className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-indigo-600 hover:bg-indigo-700 disabled:bg-gray-400 text-white rounded-xl font-medium transition-colors"
          >
            {emailSending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Mail className="w-4 h-4" />}
            {emailSending ? 'Sending…' : 'Send Report'}
          </button>
        </div>
      </Dialog>
    </div>
  );
};

export default AnalyticsPage;
